/**
 * UnifiedActionReview - Single component for ALL AI action rendering
 * 
 * Display modes based on complexity:
 * - 1 action, 1-2 fields: Inline card with field list + chevrons (no modal)
 * - 1 action, 3+ fields: Card + modal for details
 * - Multiple actions: Card + modal for selection
 * 
 * @see /app/docs/refactor/unified-action-review.md for full documentation
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { 
  Check, X, Loader2, ChevronDown, ChevronRight, 
  Plus, Edit, Trash2, Link, Unlink, GitMerge, Scissors,
  Layers, AlertCircle, Pencil, MessageSquare, ExternalLink
} from 'lucide-react';
import { getFieldLabel } from './utils/fieldLabels';
import { 
  resolveEntityTitle as resolveEntityTitleUtil,
  fetchLinkableEntities,
  extractRefEntityTypes,
  getRefFieldOptions,
  isSingleRefField,
  isMultiRefField,
  getEntityConfig,
  ENTITY_CONFIG,
} from './utils/entityResolution';
// Import unified entity ID processor for normalizing redundant prefixes
import { normalizeEntityId, parseCompositeKpiKey, isCompositeKpiKey, LINKABLE_ENTITIES } from './utils/entityIdProcessor';
import { collectionActionsApi, featuresApi, epicsApi, initiativesApi } from '../../services/api';
import { toast } from 'sonner';
import CopilotMarkdown from './CopilotMarkdown';
import MergeConfirmationModal from './MergeConfirmationModal';
import EntityLinkedText from './EntityLinkedText';
import { NON_RENDERABLE_ACTIONS } from './shared/ActionButtons';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { useEntityModal } from '../../contexts/EntityModalContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../ui/select';

// =============================================================================
// ACTION TYPE CONFIGURATION
// =============================================================================

const ACTION_CONFIG = {
  UPDATE_ITEM: {
    icon: Edit,
    label: 'Update',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  CREATE_ITEM: {
    icon: Plus,
    label: 'Create',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  DELETE_ITEM: {
    icon: Trash2,
    label: 'Delete',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  LINK_ITEMS: {
    icon: Link,
    label: 'Link',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    badgeClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
  UNLINK_ITEMS: {
    icon: Unlink,
    label: 'Unlink',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
  MERGE_ITEMS: {
    icon: GitMerge,
    label: 'Merge',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  SPLIT_ITEM: {
    icon: Scissors,
    label: 'Split',
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    badgeClass: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
  BULK_UPDATE: {
    icon: Edit,
    label: 'Bulk Update',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  BULK_CREATE: {
    icon: Plus,
    label: 'Bulk Create',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  BULK_LINK: {
    icon: Link,
    label: 'Bulk Link',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  // First-class embedded entity actions
  CREATE_MARKET_SEGMENT: {
    icon: Plus,
    label: 'Create Segment',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  UPDATE_MARKET_SEGMENT: {
    icon: Edit,
    label: 'Update Segment',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  DELETE_MARKET_SEGMENT: {
    icon: Trash2,
    label: 'Delete Segment',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  CREATE_COMPETITOR: {
    icon: Plus,
    label: 'Create Competitor',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  UPDATE_COMPETITOR: {
    icon: Edit,
    label: 'Update Competitor',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  DELETE_COMPETITOR: {
    icon: Trash2,
    label: 'Delete Competitor',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  CREATE_REQUIREMENT: {
    icon: Plus,
    label: 'Create Requirement',
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    badgeClass: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  },
  UPDATE_REQUIREMENT: {
    icon: Edit,
    label: 'Update Requirement',
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    badgeClass: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  },
  DELETE_REQUIREMENT: {
    icon: Trash2,
    label: 'Delete Requirement',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  CREATE_METRIC_ALIGNMENT: {
    icon: Plus,
    label: 'Create Alignment',
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  },
  UPDATE_METRIC_ALIGNMENT: {
    icon: Edit,
    label: 'Update Alignment',
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  },
  DELETE_METRIC_ALIGNMENT: {
    icon: Trash2,
    label: 'Delete Alignment',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  CREATE_FEATURE_LINK: {
    icon: Plus,
    label: 'Create Link',
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-900/20',
    badgeClass: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  UPDATE_FEATURE_LINK: {
    icon: Edit,
    label: 'Update Link',
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-900/20',
    badgeClass: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  DELETE_FEATURE_LINK: {
    icon: Trash2,
    label: 'Delete Link',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};

const getActionConfig = (type) => {
  return ACTION_CONFIG[type] || {
    icon: Layers,
    label: type?.replace(/_/g, ' ') || 'Action',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  };
};

// =============================================================================
// HIDDEN FIELDS - Fields that should NEVER be displayed (metadata only)
// =============================================================================

const HIDDEN_FIELDS = new Set([
  '__entity_type', 'entity_type', 'parent_id', '_id', 'product_id', 
  'created_at', 'updated_at', 'organization_id'
]);

/**
 * Extract entity type from item (handles __entity_type and entity_type)
 */
const getItemEntityType = (item, fallbackEntityType) => {
  return item.__entity_type || item.entity_type || fallbackEntityType || 'item';
};

/**
 * Get the primary display value for an item (for card title).
 * Uses flat dict format: item.updates, item.fields, or flat on item
 * Also checks for direct item.title (for BULK_UPDATE items)
 */
const getItemDisplayTitle = (item, entityType) => {
  // Check for direct title on item first (BULK_UPDATE schema includes title)
  if (item.title) {
    const str = String(item.title);
    return str.length > 60 ? str.substring(0, 60) + '...' : str;
  }
  
  const config = getEntityConfig(entityType);
  const primaryField = config.primaryField;
  
  // Get source dict: updates for UPDATE, fields for CREATE, or flat on item
  const sourceDict = item.updates || item.fields || item;
  const primaryValue = sourceDict[primaryField];
  
  if (primaryValue) {
    const str = String(primaryValue);
    return str.length > 60 ? str.substring(0, 60) + '...' : str;
  }
  
  return `New ${entityType}`;
};

/**
 * Extract display fields from item based on entity config.
 * Uses flat dict format: item.updates (UPDATE_ITEM) or item.fields (CREATE_ITEM)
 * or flat fields directly on item (BULK_CREATE items)
 */
const extractDisplayFields = (item, entityType) => {
  const config = getEntityConfig(entityType);
  const fields = [];
  
  // Get the source dict: updates for UPDATE, fields for CREATE, or flat on item
  const sourceDict = item.updates || item.fields || item;
  
  // First, add fields in the preferred order from displayFields
  for (const fieldName of config.displayFields) {
    const value = sourceDict[fieldName];
    if (value !== undefined) {
      fields.push({
        field: fieldName,
        label: config.fieldLabels[fieldName] || getFieldLabel(entityType, fieldName),
        value: value,
        operation: 'set',
      });
    }
  }
  
  // Collect all keys (for flat items, exclude metadata)
  const allKeys = Object.keys(sourceDict);
  
  // Then add any remaining fields not in displayFields (except hidden ones)
  for (const key of allKeys) {
    if (HIDDEN_FIELDS.has(key)) continue;
    if (key === 'fields' || key === 'updates' || key === 'item_id') continue;
    if (config.displayFields.includes(key)) continue; // Already added
    
    const value = sourceDict[key];
    if (value !== undefined && typeof value !== 'object') {
      fields.push({
        field: key,
        label: config.fieldLabels[key] || getFieldLabel(entityType, key),
        value: value,
        operation: 'set',
      });
    }
  }
  
  return fields;
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get display mode based on action complexity
 */
const getDisplayMode = (actions) => {
  if (actions.length === 0) return 'none';
  
  // Multiple actions always use modal
  if (actions.length > 1) return 'modal';
  
  const action = actions[0];
  const actionType = action.type;
  
  // BULK actions always use modal (they have items array, not fields)
  if (actionType?.startsWith('BULK_')) return 'modal';
  
  // SPLIT and MERGE always use modal
  if (actionType === 'SPLIT_ITEM' || actionType === 'MERGE_ITEMS') return 'modal';
  
  // All actions use modal for consistent review experience
  return 'modal';
};

/**
 * Get a human-readable title for an action.
 * Uses flat dict formats:
 * - UPDATE_ITEM: action.updates
 * - CREATE_ITEM: action.fields or namespaced fields (request.title)
 * - BULK_UPDATE items: action.title (directly on item)
 */
const getActionTitle = (action) => {
  // Use pre-computed display title if available (from prescriptive entity config)
  if (action._displayTitle) {
    return action._displayTitle;
  }
  
  // Check for title directly on the action (for BULK_UPDATE items)
  if (action.title) {
    const value = String(action.title);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  
  let entityType = action.entity_type || 'item';
  
  // For embedded entity actions (CREATE_MARKET_SEGMENT, etc.), derive entity type from action type
  const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
  if (embeddedMatch && !action.entity_type) {
    entityType = embeddedMatch[2].toLowerCase(); // e.g., "market_segment"
  }
  
  // For LINK/UNLINK, show source → target (flat structure)
  if (action.type === 'LINK_ITEMS' || action.type === 'UNLINK_ITEMS') {
    const sourceType = action.source_type || 'item';
    const targetType = action.target_type || 'item';
    return `${sourceType} → ${targetType}`;
  }
  
  // Check for namespaced title field first (e.g., "request.title", "market_segment.title")
  const namespacedTitleKey = `${entityType}.title`;
  const namespacedNameKey = `${entityType}.name`;
  const namespacedMetricKey = `${entityType}.metric_name`;
  const namespacedTextKey = `${entityType}.text`; // For requirement.text
  
  if (action[namespacedTitleKey]) {
    const value = String(action[namespacedTitleKey]);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  if (action[namespacedNameKey]) {
    const value = String(action[namespacedNameKey]);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  if (action[namespacedMetricKey]) {
    const value = String(action[namespacedMetricKey]);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  if (action[namespacedTextKey]) {
    const value = String(action[namespacedTextKey]);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  
  // Get the flat dict of fields (updates for UPDATE_ITEM, fields for CREATE_ITEM)
  const fieldsDict = action.updates || action.fields || {};
  
  // Use entity config to find primary field
  const config = getEntityConfig(entityType);
  if (fieldsDict[config.primaryField] !== undefined) {
    const value = String(fieldsDict[config.primaryField]);
    return value.length > 60 ? value.substring(0, 60) + '...' : value;
  }
  
  // Fallback: Priority fields for title
  const titleFields = ['metric_name', 'title', 'name', 'description', 'text'];
  
  for (const fieldName of titleFields) {
    if (fieldsDict[fieldName] !== undefined) {
      const value = String(fieldsDict[fieldName]);
      return value.length > 50 ? value.substring(0, 50) + '...' : value;
    }
  }
  
  // Fallback to item_id for updates
  if (action.item_id) {
    return `${entityType}: ${action.item_id}`;
  }
  
  // For embedded entities, use a more descriptive fallback
  const entityLabel = entityType.replace(/_/g, ' ');
  return `New ${entityLabel}`;
};

/**
 * Format a field value for display - NO TRUNCATION
 * Handles nested arrays and objects with proper rendering
 */
const formatValue = (value, depth = 0) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 italic">Not set</span>;
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 italic">Empty array</span>;
    }
    
    // Check if array contains objects (nested array)
    const hasObjects = value.some(item => typeof item === 'object' && item !== null);
    
    if (hasObjects) {
      // Render as expandable nested items
      return (
        <div className="space-y-2 mt-1">
          {value.map((item, index) => (
            <div 
              key={index} 
              className="border-l-2 border-indigo-200 dark:border-indigo-700 pl-3 py-1 bg-gray-50 dark:bg-gray-800/50 rounded-r"
            >
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Item {index + 1}
              </div>
              {typeof item === 'object' && item !== null ? (
                <div className="space-y-1">
                  {Object.entries(item).map(([key, val]) => (
                    <div key={key} className="flex gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 min-w-[80px]">{key}:</span>
                      <span className="text-gray-900 dark:text-gray-100">{formatValue(val, depth + 1)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span>{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    // Simple array of primitives - join with commas
    return <span>{value.join(', ')}</span>;
  }
  
  // Handle objects (non-array)
  if (typeof value === 'object') {
    return (
      <div className="space-y-2 mt-1">
        {Object.entries(value).map(([key, val]) => (
          <div key={key} className="text-sm">
            <span className="text-gray-500 dark:text-gray-400 font-medium">{key}:</span>
            <div className="text-gray-900 dark:text-gray-100 mt-0.5 pl-2">
              {formatValue(val, depth + 1)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }
  if (typeof value === 'number') {
    return <span className="font-mono">{value}</span>;
  }
  // Render string values with markdown support
  const strValue = String(value);
  // Only use markdown if the string contains markdown-like syntax
  const hasMarkdown = /[*_#`\[\]]/.test(strValue);
  if (hasMarkdown) {
    return <CopilotMarkdown content={strValue} className="text-sm" />;
  }
  // NO TRUNCATION - show full value
  return <span className="whitespace-pre-wrap">{strValue}</span>;
};

/**
 * Get counts by action type for summary display
 */
const getActionTypeCounts = (actions) => {
  const counts = {};
  actions.forEach(action => {
    const type = action.type || 'UNKNOWN';
    counts[type] = (counts[type] || 0) + 1;
  });
  return counts;
};

/**
 * Get counts by action type + entity type for better summary display
 * Returns something like: 
 * { 
 *   'create_request': { count: 2, actionType: 'CREATE_ITEM' },
 *   'create_competitor': { count: 2, actionType: 'CREATE_COMPETITOR' }
 * }
 * This allows showing proper labels AND using correct badge colors
 */
const getEntityTypeCounts = (actions) => {
  const counts = {};
  actions.forEach(action => {
    // Handle CREATE_ITEM, UPDATE_ITEM, and DELETE_ITEM with entity_type
    if ((action.type === 'CREATE_ITEM' || action.type === 'UPDATE_ITEM' || action.type === 'DELETE_ITEM') && action.entity_type) {
      const actionVerb = action.type === 'CREATE_ITEM' ? 'create' : action.type === 'UPDATE_ITEM' ? 'update' : 'delete';
      const key = `${actionVerb}_${action.entity_type}`;
      if (!counts[key]) {
        counts[key] = { count: 0, actionType: action.type };
      }
      counts[key].count += 1;
    }
    // Handle embedded entity actions like CREATE_COMPETITOR, UPDATE_MARKET_SEGMENT, etc.
    else {
      const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
      if (embeddedMatch && embeddedMatch[2] !== 'ITEM') {
        const verb = embeddedMatch[1].toLowerCase();
        const entityType = embeddedMatch[2].toLowerCase().replace(/_/g, ' ');
        const key = `${verb}_${entityType}`;
        if (!counts[key]) {
          counts[key] = { count: 0, actionType: action.type }; // Store original like CREATE_COMPETITOR
        }
        counts[key].count += 1;
      }
    }
  });
  return counts;
};

/**
 * Format entity type counts for display (e.g., "1 Goal, 4 KPIs")
 */
const formatEntityCounts = (counts) => {
  return Object.entries(counts).map(([type, count]) => {
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    const plural = count > 1 ? 's' : '';
    return `${count} ${label}${plural}`;
  }).join(', ');
};

// =============================================================================
// ACTION ITEM COMPONENT (for modal list)
// =============================================================================

/**
 * Normalize fields/updates to array format for rendering.
 * Handles three formats:
 * 1. Namespaced fields: {"request.title": "...", "request.priority": "High"} → strip prefix
 * 2. Nested fields: {fields: {title: "...", priority: "High"}} → extract from fields
 * 3. Legacy flat dict: {title: "...", priority: "High"} → use directly
 * 4. Embedded entity actions: {"market_segment.title": "..."} → detect from action type
 * 
 * Output: [{field: 'title', value: '...', label: 'Title'}, ...]
 */
const normalizeFieldsForDisplay = (action) => {
  // For embedded entity actions (CREATE_MARKET_SEGMENT, etc.), derive entity type from action type
  let entityType = action.entity_type || 'item';
  let entityPrefix = `${entityType}.`;
  
  // Check if this is a first-class embedded entity action
  // Pattern: CREATE_MARKET_SEGMENT, UPDATE_COMPETITOR, DELETE_REQUIREMENT, etc.
  const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
  if (embeddedMatch && !action.entity_type) {
    const derivedEntityType = embeddedMatch[2].toLowerCase(); // e.g., "market_segment"
    entityType = derivedEntityType;
    entityPrefix = `${derivedEntityType}.`;
  }
  
  const config = getEntityConfig(entityType);
  
  // System fields that should NOT be displayed as entity fields
  const systemFields = ['type', 'entity_type', 'parent_id', 'reason', 'action_id', 'item_id', '_dependsOnPrevious', '_displayTitle'];
  
  // First, check for namespaced fields directly on the action (new format)
  // e.g., {"request.title": "...", "request.priority": "High"}
  // or   {"market_segment.title": "..."}
  const namespacedFields = [];
  for (const [key, value] of Object.entries(action)) {
    if (key.startsWith(entityPrefix)) {
      const fieldName = key.slice(entityPrefix.length);
      namespacedFields.push({
        field: fieldName,
        label: config.fieldLabels?.[fieldName] || getFieldLabel(entityType, fieldName),
        value: value,
        operation: 'set'
      });
    }
  }
  
  // If we found namespaced fields, use them
  if (namespacedFields.length > 0) {
    return namespacedFields;
  }
  
  // Otherwise, check for nested fields object or updates dict (legacy formats)
  const sourceDict = action.updates || action.fields || {};
  
  // If sourceDict is empty, check for flat fields on the action itself
  if (Object.keys(sourceDict).length === 0) {
    const flatFields = [];
    for (const [key, value] of Object.entries(action)) {
      if (!systemFields.includes(key) && !key.includes('.') && value !== undefined && value !== null) {
        flatFields.push({
          field: key,
          label: config.fieldLabels?.[key] || getFieldLabel(entityType, key),
          value: value,
          operation: 'set'
        });
      }
    }
    return flatFields;
  }
  
  // Convert sourceDict to array format for rendering
  return Object.entries(sourceDict).map(([fieldName, value]) => ({
    field: fieldName,
    label: config.fieldLabels?.[fieldName] || getFieldLabel(entityType, fieldName),
    value: value,
    operation: 'set'
  }));
};

const ActionItem = ({
  action,
  actionIdx,
  isSelected,
  onToggle,
  isExpanded,
  onToggleExpand,
  // NEW: For UPDATE comparison and inline editing
  originalValues = {},
  editedValues = {},
  editingField = null,
  onStartEditing,
  onSaveEdit,
  onCancelEditing,
  getEffectiveValue,
  isFieldEdited,
  loadingOriginals = false,
  fieldSchemas = {},
  linkableEntities = {},
  // NEW: For scoped KPI dropdowns
  scopedKpis = {},
  onFetchScopedKpis,
  getScopedKpiOptions,
}) => {
  const config = getActionConfig(action.type);
  const Icon = config.icon;
  const fields = normalizeFieldsForDisplay(action);
  const { openEntityModal } = useEntityModal();
  
  // Local state for edit input
  const [editValue, setEditValue] = useState('');
  
  // For LINK/UNLINK actions, show source/target info
  const isLinkAction = action.type === 'LINK_ITEMS' || action.type === 'UNLINK_ITEMS';
  
  // Helper to resolve entity title from originalValues for LINK/UNLINK actions
  const resolveLinkEntityTitle = useCallback((entityId) => {
    if (!entityId || !originalValues) return null;
    const normalizedId = normalizeEntityId(entityId);
    const entityData = originalValues[normalizedId] || originalValues[entityId];
    if (!entityData) return null;
    return entityData.title || entityData.name || entityData.metric_name || null;
  }, [originalValues]);
  
  // Pre-resolve titles for LINK actions (used in header AND expanded section)
  const sourceTitleResolved = isLinkAction ? resolveLinkEntityTitle(action.source_id) : null;
  const targetTitleResolved = isLinkAction ? resolveLinkEntityTitle(action.target_id) : null;
  
  // Check if entity type is linkable (has a modal) and get the prefix for ID extraction
  const isEntityLinkable = useCallback((entityType) => {
    if (!entityType) return false;
    // Check if the entity type matches any linkable prefix
    return !!LINKABLE_ENTITIES[entityType];
  }, []);
  
  // Extract entity ID from prefixed ID (e.g., "feat_abc123" -> "abc123" for modal opening)
  // Returns { modalType, entityId } or null if not linkable
  const getLinkInfo = useCallback((entityType, prefixedId) => {
    if (!entityType || !prefixedId) return null;
    const config = LINKABLE_ENTITIES[entityType];
    if (!config) return null;
    return { modalType: config.type, entityId: prefixedId };
  }, []);
  
  // Check if this is an UPDATE action (for comparison view)
  const isUpdateAction = action.type === 'UPDATE_ITEM' || action.type?.startsWith('UPDATE_');
  const isDeleteAction = action.type === 'DELETE_ITEM' || action.type?.startsWith('DELETE_');
  const needsOriginalData = isUpdateAction || isDeleteAction;
  
  // Derive entity type for display and field label lookup
  // For embedded entity actions (CREATE_MARKET_SEGMENT, etc.), derive from action type
  let derivedEntityType = action.entity_type || '';
  const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
  if (embeddedMatch && !action.entity_type) {
    derivedEntityType = embeddedMatch[2].toLowerCase(); // Keep underscores for field label lookup
  }
  const displayEntityType = derivedEntityType.replace(/_/g, ' '); // For display only
  
  // Get the item_id for looking up original values - try normalized ID first
  // Uses unified normalizeEntityId from entityIdProcessor to handle redundant prefixes
  // like "product_prod_xxx" -> "prod_xxx" (common LLM output issue)
  const itemId = action.item_id;
  const normalizedItemId = normalizeEntityId(itemId);
  // Look up original values - try normalized ID first, then original
  const originalData = normalizedItemId ? (originalValues[normalizedItemId] || originalValues[itemId]) : null;
  
  // Get field schema for this entity type
  const entityType = action.entity_type || derivedEntityType;
  const entityFieldSchema = fieldSchemas[entityType] || {};
  
  // Compute title - prefer original data for UPDATE/DELETE actions to show real entity name
  const title = useMemo(() => {
    // For UPDATE/DELETE actions, try to get the real name from originalData
    if (needsOriginalData && originalData) {
      const entityConfig = getEntityConfig(action.entity_type || derivedEntityType);
      const primaryField = entityConfig.primaryField;
      if (originalData[primaryField]) {
        const value = String(originalData[primaryField]);
        return value.length > 60 ? value.substring(0, 60) + '...' : value;
      }
      // Fallback to common name fields
      const nameFields = ['name', 'title', 'metric_name'];
      for (const field of nameFields) {
        if (originalData[field]) {
          const value = String(originalData[field]);
          return value.length > 60 ? value.substring(0, 60) + '...' : value;
        }
      }
    }
    // Fall back to the standard getActionTitle logic
    return getActionTitle(action);
  }, [action, needsOriginalData, originalData, derivedEntityType]);
  
  // Format entity type for display (capitalize first letter of each word)
  const entityTypeDisplay = derivedEntityType
    ? derivedEntityType.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';
  
  // Check if this item depends on a previous item (for BULK_CREATE chains)
  const dependsOnPrevious = action._dependsOnPrevious;
  
  // Build action label - include entity type for clarity
  // For real embedded entity actions (CREATE_MARKET_SEGMENT), config.label already includes type
  // For standard actions (CREATE_ITEM, UPDATE_ITEM), append entity type from action.entity_type
  const isRealEmbeddedEntity = embeddedMatch && embeddedMatch[2] !== 'ITEM';
  let actionLabel = config.label;
  if (isRealEmbeddedEntity) {
    // Real embedded entity - label already complete, e.g., "Create Segment"
    actionLabel = config.label;
  } else if (entityTypeDisplay) {
    // Standard action with entity_type - append it, e.g., "Create Request"
    actionLabel = `${config.label} ${entityTypeDisplay}`;
  }
  
  // Handle starting edit
  const handleStartEditing = (field, currentValue) => {
    // For arrays, keep as array for multi-select
    if (Array.isArray(currentValue)) {
      setEditValue(currentValue);
    } else {
      setEditValue(currentValue?.toString() || '');
    }
    onStartEditing?.(actionIdx, field);
  };
  
  // Handle save edit
  const handleSaveEdit = (field) => {
    onSaveEdit?.(actionIdx, field, editValue);
  };
  
  // Handle key press in edit mode
  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(field);
    } else if (e.key === 'Escape') {
      onCancelEditing?.();
    }
  };
  
  // Check if currently editing a specific field
  const isEditingThisField = (field) => {
    return editingField?.actionIdx === actionIdx && editingField?.field === field;
  };
  
  // ==========================================================================
  // REF_ENTITY HELPERS - Using shared utilities with local context
  // ==========================================================================
  
  // Wrapper around shared utility with local linkableEntities
  // For KPIs: returns breadcrumb format "Goal Name / KPI Name"
  const resolveEntityTitle = (entityId, refEntityType, options = {}) => {
    if (!entityId || !refEntityType) return entityId;
    
    const { breadcrumb = false } = options;
    
    // Special handling for KPIs with compound key format "goal_id:kpi_id"
    if (refEntityType === 'kpi' && isCompositeKpiKey(entityId)) {
      const entities = linkableEntities[refEntityType] || [];
      const kpi = entities.find(e => e.kpi_key === entityId);
      if (kpi) {
        // Return breadcrumb format for display
        if (breadcrumb) {
          return {
            goalTitle: kpi.goal_title || 'Unknown Goal',
            kpiName: kpi.metric_name || 'Unnamed KPI',
            full: `${kpi.goal_title || 'Unknown Goal'} / ${kpi.metric_name || 'Unnamed KPI'}`,
          };
        }
        // Simple format - just KPI name
        return kpi.metric_name || kpi.title || entityId;
      }
      // If not found in linkableEntities, parse the key for display
      const parsed = parseCompositeKpiKey(entityId);
      if (parsed && breadcrumb) {
        return {
          goalTitle: parsed.goalId,
          kpiName: parsed.kpiId,
          full: `${parsed.goalId} / ${parsed.kpiId}`,
        };
      }
      return entityId;
    }
    
    return resolveEntityTitleUtil(entityId, refEntityType, linkableEntities);
  };
  
  // Format enum value to human-readable label (e.g., "in_progress" -> "In Progress")
  const formatEnumLabel = (value) => {
    if (!value) return value;
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Format value with ID resolution for ref_entity fields
  // For KPIs: renders breadcrumb tags "Goal Name / KPI Name"
  const formatValueWithResolution = (value, fieldName) => {
    const fieldKey = fieldName.split('.').pop();
    const fieldConfig = entityFieldSchema[fieldKey];
    const refEntityType = fieldConfig?.ref_entity;
    
    // =========================================================================
    // FIRST: Check for KPI composite keys BY FORMAT (before schema check)
    // This handles cases where schema isn't loaded for kpi_alignment entity
    // KPI composite keys have format: goal_xxx:kpi_xxx
    // =========================================================================
    
    // Handle single KPI composite key (most common case in kpi_alignment)
    if (typeof value === 'string' && isCompositeKpiKey(value)) {
      const resolved = resolveEntityTitle(value, 'kpi', { breadcrumb: true });
      const isBreadcrumb = typeof resolved === 'object';
      
      return (
        <span className="inline-flex items-center gap-0 text-xs" title={value}>
          {isBreadcrumb ? (
            <>
              <span className="px-1.5 py-0.5 rounded-l bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {resolved.goalTitle}
              </span>
              <span className="px-1.5 py-0.5 rounded-r bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {resolved.kpiName}
              </span>
            </>
          ) : (
            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              {resolved}
            </span>
          )}
        </span>
      );
    }
    
    // Handle arrays that might contain KPI composite keys
    if (Array.isArray(value) && value.length > 0) {
      const firstItem = typeof value[0] === 'object' ? (value[0].kpi_key || value[0]) : value[0];
      if (typeof firstItem === 'string' && isCompositeKpiKey(firstItem)) {
        return (
          <div className="flex flex-wrap gap-1.5">
            {value.map((id, idx) => {
              const entityKey = typeof id === 'object' ? (id.kpi_key || id) : id;
              const resolved = resolveEntityTitle(entityKey, 'kpi', { breadcrumb: true });
              const isBreadcrumb = typeof resolved === 'object';
              
              return (
                <span 
                  key={idx}
                  className="inline-flex items-center gap-0 text-xs"
                  title={entityKey}
                >
                  {isBreadcrumb ? (
                    <>
                      <span className="px-1.5 py-0.5 rounded-l bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        {resolved.goalTitle}
                      </span>
                      <span className="px-1.5 py-0.5 rounded-r bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                        {resolved.kpiName}
                      </span>
                    </>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                      {resolved}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        );
      }
    }
    
    // =========================================================================
    // THEN: Handle enum fields and schema-based ref_entity resolution
    // =========================================================================
    
    // Handle enum fields - show label instead of raw value
    if (fieldConfig?.type === 'enum' && typeof value === 'string') {
      return formatEnumLabel(value);
    }
    
    if (!refEntityType) {
      return formatValue(value);
    }
    
    // Handle arrays of entity IDs (non-KPI, schema-based)
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">None linked</span>;
      }
      
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((id, idx) => {
            const title = resolveEntityTitle(id, refEntityType);
            const isResolved = title !== id;
            return (
              <span 
                key={idx}
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs",
                  isResolved 
                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 italic"
                )}
                title={id}
              >
                {isResolved ? title : `Unknown ID (${id})`}
              </span>
            );
          })}
        </div>
      );
    }
    
    // Handle single entity ID
    if (typeof value === 'string' && value.includes('_')) {
      const title = resolveEntityTitle(value, refEntityType);
      return title !== value ? (
        <span title={value}>{title}</span>
      ) : (
        <span className="text-gray-500 italic">Unknown ID ({value})</span>
      );
    }
    
    return formatValue(value);
  };
  
  // Check if field is ref entity array using shared utility
  const isRefEntityArrayField = (fieldName) => {
    const fieldKey = fieldName.split('.').pop();
    const fieldConfig = entityFieldSchema[fieldKey];
    return isMultiRefField(fieldConfig);
  };
  
  // Check if field is single ref entity using shared utility
  const isRefEntitySingleField = (fieldName) => {
    const fieldKey = fieldName.split('.').pop();
    const fieldConfig = entityFieldSchema[fieldKey];
    return isSingleRefField(fieldConfig);
  };
  
  // Get options for ref entity field using shared utility
  const getRefEntityOptionsLocal = (fieldName) => {
    const fieldKey = fieldName.split('.').pop();
    const fieldConfig = entityFieldSchema[fieldKey];
    const refEntityType = fieldConfig?.ref_entity;
    
    if (!refEntityType) return [];
    return getRefFieldOptions(refEntityType, linkableEntities);
  };

  return (
    <div 
      className={cn(
        "border rounded-lg transition-all overflow-hidden",
        isSelected 
          ? "bg-white dark:bg-gray-800 border-indigo-300 dark:border-indigo-700 shadow-sm" 
          : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-50"
      )}
    >
      {/* Header */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
          config.bgColor
        )}
        onClick={onToggleExpand}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0"
        />
        
        <Badge variant="outline" className={cn("text-xs flex items-center gap-1 flex-shrink-0", config.badgeClass)}>
          <Icon className="w-3 h-3" />
          {actionLabel}
        </Badge>
        
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">
            {title}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {isLinkAction 
              ? `${sourceTitleResolved || action.source_id} → ${targetTitleResolved || action.target_id}`
              : (
                <>
                  {fields.length} {fields.length === 1 ? 'field' : 'fields'}
                  {dependsOnPrevious && (
                    <span className="ml-1 text-indigo-500 dark:text-indigo-400">
                      • linked to item above
                    </span>
                  )}
                </>
              )
            }
          </span>
        </div>
        
        <button
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>
      
      {/* AI Reason - shown for all actions when present (outside expanded area) */}
      {action.reason && !isLinkAction && (
        <div className="px-3 pb-2 pt-1">
          <div className="flex items-start gap-2 py-2 px-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
              <EntityLinkedText text={action.reason} />
            </span>
          </div>
        </div>
      )}
      
      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-850">
          <div className="p-3 space-y-2">
            {/* Reference Description - shown for UPDATE actions when description is NOT being updated */}
            {isUpdateAction && originalData?.description && !fields.some(f => f.field === 'description') && (
              <div className="py-2 px-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    Description
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700">
                    reference only
                  </Badge>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                  {originalData.description}
                </div>
              </div>
            )}
            
            {isLinkAction ? (
              // Link action details - show resolved titles with clickable links
              (() => {
                // Format type for display (e.g., "feature" -> "Feature")
                const formatType = (type) => type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
                
                // Get link info for source and target
                const sourceLinkInfo = getLinkInfo(action.source_type, action.source_id);
                const targetLinkInfo = getLinkInfo(action.target_type, action.target_id);
                
                // Render entity display with optional clickable link
                const renderEntityLink = (title, linkInfo, fallbackId) => {
                  const displayText = title || fallbackId;
                  if (linkInfo) {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openEntityModal(linkInfo.modalType, linkInfo.entityId);
                        }}
                        className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                        title={`View ${linkInfo.modalType}: ${linkInfo.entityId}`}
                      >
                        {displayText}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    );
                  }
                  return <span className="font-medium">{displayText}</span>;
                };
                
                return (
                  <>
                    <div className="flex items-start gap-3 py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-[80px]">Source</span>
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        <span className="text-gray-500 dark:text-gray-400">{formatType(action.source_type)}:</span>{' '}
                        {renderEntityLink(sourceTitleResolved, sourceLinkInfo, action.source_id)}
                      </span>
                    </div>
                    <div className="flex items-start gap-3 py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-[80px]">Target</span>
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        <span className="text-gray-500 dark:text-gray-400">{formatType(action.target_type)}:</span>{' '}
                        {renderEntityLink(targetTitleResolved, targetLinkInfo, action.target_id)}
                      </span>
                    </div>
                    {action.reason && (
                      <div className="flex items-start gap-3 py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-[80px]">Reason</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">{action.reason}</span>
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              // Field-based action details with comparison for UPDATE actions
              fields.map((field, idx) => {
                const fieldName = field.field;
                // Strip namespace prefix (e.g., "product.high_level_vision" -> "high_level_vision")
                const strippedFieldName = fieldName.includes('.') ? fieldName.split('.').pop() : fieldName;
                const newValue = getEffectiveValue?.(actionIdx, fieldName, field.value) ?? field.value;
                // Look up original value using stripped field name
                const originalValue = originalData?.[strippedFieldName];
                // Show CURRENT/NEW view for UPDATE actions when we have originalData
                // Even if the field is missing or null, users should see the current state
                const hasOriginal = isUpdateAction && originalData;
                const isEditing = isEditingThisField(fieldName);
                const wasEdited = isFieldEdited?.(actionIdx, fieldName);
                const valueChanged = hasOriginal && originalValue !== newValue;
                
                return (
                  <div 
                    key={idx} 
                    className="py-2 px-2 rounded bg-gray-50 dark:bg-gray-800"
                  >
                    {/* Field label - prefer backend schema label (using stripped name), then static labels, then field name */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {entityFieldSchema[strippedFieldName]?.label || field.label || getFieldLabel(derivedEntityType || action.entity_type, strippedFieldName)}
                      </span>
                      {field.operation && field.operation !== 'set' && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {field.operation}
                        </Badge>
                      )}
                      {wasEdited && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-200">
                          edited
                        </Badge>
                      )}
                    </div>
                    
                    {/* For UPDATE actions: show comparison view */}
                    {isUpdateAction && hasOriginal ? (
                      <div className="space-y-1">
                        {/* Current/Original value */}
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 min-w-[60px] pt-0.5">
                            Current
                          </span>
                          <div className="text-sm flex-1 text-gray-400 dark:text-gray-500">
                            {formatValueWithResolution(originalValue, fieldName) || <span className="italic text-gray-400">none</span>}
                          </div>
                        </div>
                        
                        {/* New/Proposed value */}
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 min-w-[60px] pt-0.5">
                            New
                          </span>
                          
                          {isEditing ? (
                            // Edit mode - replaces the entire value area
                            <div className="flex-1">
                              {(() => {
                                const fieldSchema = entityFieldSchema[fieldName];
                                const isEnum = fieldSchema?.type === 'enum' && Array.isArray(fieldSchema?.values);
                                const isRefArray = isRefEntityArrayField(fieldName);
                                const isRefSingle = isRefEntitySingleField(fieldName);
                                
                                if (isEnum) {
                                  // Render dropdown for enum fields
                                  return (
                                    <div className="flex items-center gap-2">
                                      <Select
                                        value={editValue}
                                        onValueChange={(val) => {
                                          setEditValue(val);
                                          // Auto-save on select
                                          onSaveEdit?.(actionIdx, fieldName, val);
                                          onCancelEditing?.();
                                        }}
                                      >
                                        <SelectTrigger className="flex-1 text-sm h-8">
                                          <SelectValue placeholder="Select value">
                                            {formatEnumLabel(editValue)}
                                          </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                          {fieldSchema.values.map((val) => (
                                            <SelectItem key={val} value={val}>
                                              {formatEnumLabel(val)}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={onCancelEditing}
                                      >
                                        <X className="w-3 h-3 text-gray-400" />
                                      </Button>
                                    </div>
                                  );
                                }
                                
                                // Special case for goal_ids, subscribed_kpi_ids, kpi_alignments - tag-style remove-only
                                // Check BEFORE isRefArray since schema might not be loaded
                                const fieldKey = fieldName.split('.').pop();
                                if (fieldKey === 'goal_ids' || fieldKey === 'subscribed_kpi_ids' || fieldKey === 'kpi_alignments') {
                                  const currentValues = Array.isArray(editValue) ? editValue : (editValue ? [editValue] : []);
                                  const refEntityType = fieldKey === 'goal_ids' ? 'goal' : 'kpi';
                                  const emptyLabel = fieldKey === 'goal_ids' ? 'No goals selected' : 'No KPIs selected';
                                  const tagColorClass = fieldKey === 'goal_ids' 
                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                    : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300';
                                  
                                  return (
                                    <div className="flex flex-col gap-2 w-full">
                                      <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-gray-800 rounded-md min-h-[40px] border border-gray-200 dark:border-gray-700">
                                        {currentValues.length === 0 ? (
                                          <span className="text-xs text-gray-400 italic">{emptyLabel}</span>
                                        ) : (
                                          currentValues.map((val, idx) => {
                                            const entityKey = typeof val === 'object' ? (val.kpi_key || val.goal_id) : val;
                                            const displayName = resolveEntityTitle(entityKey, refEntityType);
                                            return (
                                              <span 
                                                key={idx}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${tagColorClass}`}
                                              >
                                                {displayName}
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newValues = currentValues.filter((_, i) => i !== idx);
                                                    setEditValue(newValues);
                                                  }}
                                                  className="ml-0.5 hover:text-red-500 transition-colors"
                                                >
                                                  <X className="w-3 h-3" />
                                                </button>
                                              </span>
                                            );
                                          })
                                        )}
                                      </div>
                                      <div className="flex justify-end gap-1">
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 px-2 text-xs"
                                          onClick={() => {
                                            onSaveEdit?.(actionIdx, fieldName, editValue);
                                          }}
                                        >
                                          <Check className="w-3 h-3 mr-1 text-emerald-600" />
                                          Save
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 px-2 text-xs"
                                          onClick={onCancelEditing}
                                        >
                                          <X className="w-3 h-3 mr-1 text-gray-400" />
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                // Special case for kpi_key (single KPI reference) - use scoped KPI dropdown
                                // This handles KPI alignment creation where user picks a single KPI
                                if (fieldKey === 'kpi_key') {
                                  // Determine parent entity context for scoped KPI fetch
                                  const parentEntityId = action.parent_id || action.item_id;
                                  const parentEntityType = action.parent_entity_type || action.entity_type || 'feature';
                                  
                                  // Trigger scoped KPI fetch if needed
                                  const scopedData = scopedKpis?.[parentEntityId];
                                  if (!scopedData?.fetched && !scopedData?.loading && onFetchScopedKpis && parentEntityId) {
                                    onFetchScopedKpis(parentEntityType, parentEntityId);
                                  }
                                  
                                  // Get options (scoped if available, fallback to all)
                                  const kpiOptionsData = getScopedKpiOptions?.(parentEntityType, parentEntityId) || { grouped: false, options: [] };
                                  
                                  return (
                                    <div className="flex items-center gap-2 w-full">
                                      <Select
                                        value={editValue || ''}
                                        onValueChange={(val) => {
                                          setEditValue(val);
                                          onSaveEdit?.(actionIdx, fieldName, val);
                                          onCancelEditing?.();
                                        }}
                                      >
                                        <SelectTrigger className="flex-1 text-sm h-8">
                                          <SelectValue placeholder={scopedData?.loading ? "Loading KPIs..." : "Select KPI..."}>
                                            {editValue && (() => {
                                              // Format selected value as breadcrumb
                                              const resolved = resolveEntityTitle(editValue, 'kpi', { breadcrumb: true });
                                              return typeof resolved === 'object' 
                                                ? resolved.full 
                                                : resolved;
                                            })()}
                                          </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[300px]">
                                          {kpiOptionsData.grouped ? (
                                            // Grouped by source (Epic or Tag Initiative)
                                            Object.entries(kpiOptionsData.sources).map(([sourceKey, sourceData]) => (
                                              <SelectGroup key={sourceKey}>
                                                <SelectLabel className="text-xs text-gray-500 font-medium px-2 py-1.5">
                                                  {sourceKey === 'epic' ? '📦 From Epic' : '🏷️ From Tag Initiative'}: {sourceData.name}
                                                </SelectLabel>
                                                {sourceData.kpis.map((opt) => (
                                                  <SelectItem key={opt.value} value={opt.value} className="pl-4">
                                                    <span className="flex items-center gap-1">
                                                      <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                      <span className="text-gray-400">/</span>
                                                      <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                                      {opt.isAligned && (
                                                        <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-green-100 text-green-700">aligned</span>
                                                      )}
                                                    </span>
                                                  </SelectItem>
                                                ))}
                                              </SelectGroup>
                                            ))
                                          ) : (
                                            // Flat list (fallback)
                                            kpiOptionsData.options?.map((opt) => (
                                              <SelectItem key={opt.value} value={opt.value}>
                                                <span className="flex items-center gap-1">
                                                  <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                  <span className="text-gray-400">/</span>
                                                  <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                                </span>
                                              </SelectItem>
                                            ))
                                          )}
                                          {(!kpiOptionsData.grouped && (!kpiOptionsData.options || kpiOptionsData.options.length === 0)) && (
                                            <div className="px-3 py-2 text-sm text-gray-500 italic">
                                              No KPIs available. Link goals first.
                                            </div>
                                          )}
                                        </SelectContent>
                                      </Select>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={onCancelEditing}
                                      >
                                        <X className="w-3 h-3 text-gray-400" />
                                      </Button>
                                    </div>
                                  );
                                }
                                
                                if (isRefArray) {
                                  // Render multi-select for other ref_entity array fields (e.g., persona_ids, tag_ids)
                                  const options = getRefEntityOptionsLocal(fieldName);
                                  return (
                                    <div className="flex items-start gap-2 w-full">
                                      <select
                                        multiple
                                        value={Array.isArray(editValue) ? editValue : (editValue ? [editValue] : [])}
                                        onChange={(e) => {
                                          const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                          setEditValue(selected);
                                        }}
                                        className="flex-1 text-sm min-h-[120px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
                                      >
                                        {options.map(opt => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="flex flex-col gap-1">
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={() => {
                                            const valToSave = Array.isArray(editValue) ? editValue : (editValue ? [editValue] : []);
                                            onSaveEdit?.(actionIdx, fieldName, valToSave);
                                          }}
                                        >
                                          <Check className="w-3 h-3 text-emerald-600" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={onCancelEditing}
                                        >
                                          <X className="w-3 h-3 text-gray-400" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                } else if (isRefSingle) {
                                  // Render single-select for ref_entity single fields (e.g., owner_id)
                                  const fieldSchema = entityFieldSchema[fieldKey];
                                  const refEntityType = fieldSchema?.ref_entity;
                                  
                                  // Special handling for kpi_key - use scoped KPIs with grouped dropdown
                                  if (fieldKey === 'kpi_key' && refEntityType === 'kpi') {
                                    // Determine parent entity context for scoped KPI fetch
                                    const parentEntityId = action.parent_id || action.item_id;
                                    const parentEntityType = action.parent_entity_type || action.entity_type || 'feature';
                                    
                                    // Trigger scoped KPI fetch if needed
                                    const scopedData = scopedKpis?.[parentEntityId];
                                    if (!scopedData?.fetched && !scopedData?.loading && onFetchScopedKpis && parentEntityId) {
                                      onFetchScopedKpis(parentEntityType, parentEntityId);
                                    }
                                    
                                    // Get options (scoped if available, fallback to all)
                                    const kpiOptionsData = getScopedKpiOptions?.(parentEntityType, parentEntityId) || { grouped: false, options: [] };
                                    
                                    return (
                                      <div className="flex items-center gap-2 w-full">
                                        <Select
                                          value={editValue || ''}
                                          onValueChange={(val) => {
                                            setEditValue(val);
                                            onSaveEdit?.(actionIdx, fieldName, val);
                                            onCancelEditing?.();
                                          }}
                                        >
                                          <SelectTrigger className="flex-1 text-sm h-8">
                                            <SelectValue placeholder={scopedData?.loading ? "Loading KPIs..." : "Select KPI..."}>
                                              {editValue && (() => {
                                                // Format selected value as breadcrumb
                                                const resolved = resolveEntityTitle(editValue, 'kpi', { breadcrumb: true });
                                                return typeof resolved === 'object' 
                                                  ? resolved.full 
                                                  : resolved;
                                              })()}
                                            </SelectValue>
                                          </SelectTrigger>
                                          <SelectContent className="max-h-[300px]">
                                            {kpiOptionsData.grouped ? (
                                              // Grouped by source (Epic or Tag Initiative)
                                              Object.entries(kpiOptionsData.sources).map(([sourceKey, sourceData]) => (
                                                <SelectGroup key={sourceKey}>
                                                  <SelectLabel className="text-xs text-gray-500 font-medium px-2 py-1.5">
                                                    {sourceKey === 'epic' ? '📦 From Epic' : '🏷️ From Tag Initiative'}: {sourceData.name}
                                                  </SelectLabel>
                                                  {sourceData.kpis.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value} className="pl-4">
                                                      <span className="flex items-center gap-1">
                                                        <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                        <span className="text-gray-400">/</span>
                                                        <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                                        {opt.isAligned && (
                                                          <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-green-100 text-green-700">aligned</span>
                                                        )}
                                                      </span>
                                                    </SelectItem>
                                                  ))}
                                                </SelectGroup>
                                              ))
                                            ) : (
                                              // Flat list (fallback)
                                              kpiOptionsData.options?.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                  <span className="flex items-center gap-1">
                                                    <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                    <span className="text-gray-400">/</span>
                                                    <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                                  </span>
                                                </SelectItem>
                                              ))
                                            )}
                                            {(!kpiOptionsData.grouped && (!kpiOptionsData.options || kpiOptionsData.options.length === 0)) && (
                                              <div className="px-3 py-2 text-sm text-gray-500 italic">
                                                No KPIs available. Link goals first.
                                              </div>
                                            )}
                                          </SelectContent>
                                        </Select>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0 flex-shrink-0"
                                          onClick={onCancelEditing}
                                        >
                                          <X className="w-3 h-3 text-gray-400" />
                                        </Button>
                                      </div>
                                    );
                                  }
                                  
                                  // Default single-select for other ref_entity fields
                                  const options = getRefEntityOptionsLocal(fieldName);
                                  return (
                                    <div className="flex items-center gap-2">
                                      <Select
                                        value={editValue || ''}
                                        onValueChange={(val) => {
                                          setEditValue(val);
                                          onSaveEdit?.(actionIdx, fieldName, val);
                                          onCancelEditing?.();
                                        }}
                                      >
                                        <SelectTrigger className="flex-1 text-sm h-8">
                                          <SelectValue placeholder="Select..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {options.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                              {opt.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={onCancelEditing}
                                      >
                                        <X className="w-3 h-3 text-gray-400" />
                                      </Button>
                                    </div>
                                  );
                                } else {
                                  // Render text input for other fields
                                  // Use textarea for hypothesis fields (always multi-line)
                                  const useTextarea = strippedFieldName === 'hypothesis' || String(newValue).length > 50;
                                  return (
                                    <div className="flex items-start gap-2">
                                      {useTextarea ? (
                                        <Textarea
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => handleKeyDown(e, fieldName)}
                                          className="flex-1 text-sm"
                                          rows={3}
                                          autoFocus
                                        />
                                      ) : (
                                        <Input
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => handleKeyDown(e, fieldName)}
                                          className="flex-1 text-sm h-8"
                                          autoFocus
                                        />
                                      )}
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={() => handleSaveEdit(fieldName)}
                                      >
                                        <Check className="w-3 h-3 text-emerald-600" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 flex-shrink-0"
                                        onClick={onCancelEditing}
                                      >
                                        <X className="w-3 h-3 text-gray-400" />
                                      </Button>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          ) : (
                            // Display mode with edit button
                            <div className="flex-1 flex items-start gap-2 group">
                              <div className={cn(
                                "text-sm flex-1",
                                valueChanged 
                                  ? "text-emerald-700 dark:text-emerald-300 font-medium" 
                                  : "text-gray-900 dark:text-gray-100"
                              )}>
                                {formatValueWithResolution(newValue, fieldName)}
                              </div>
                              <button
                                onClick={() => handleStartEditing(fieldName, newValue)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit value"
                              >
                                <Pencil className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : loadingOriginals && isUpdateAction && !originalData ? (
                      // Loading state for UPDATE actions (only show if we haven't loaded entity data yet)
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading current value...
                      </div>
                    ) : (
                      // Standard display (CREATE actions or no original available)
                      <div className="flex items-start gap-2 group">
                        {/* Allow editing even for CREATE actions */}
                        {isEditing ? (
                          <div className="flex items-start gap-2 w-full">
                            {/* Special case for goal_ids, subscribed_kpi_ids, kpi_alignments - tag-style remove-only */}
                            {(() => {
                              const fieldKey = fieldName.split('.').pop();
                              if (fieldKey === 'goal_ids' || fieldKey === 'subscribed_kpi_ids' || fieldKey === 'kpi_alignments') {
                                const currentValues = Array.isArray(editValue) ? editValue : (editValue ? [editValue] : []);
                                const refEntityType = fieldKey === 'goal_ids' ? 'goal' : 'kpi';
                                const emptyLabel = fieldKey === 'goal_ids' ? 'No goals selected' : 'No KPIs selected';
                                const tagColorClass = fieldKey === 'goal_ids' 
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                  : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300';
                                
                                return (
                                  <div className="flex flex-col gap-2 w-full">
                                    <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-gray-800 rounded-md min-h-[40px] border border-gray-200 dark:border-gray-700">
                                      {currentValues.length === 0 ? (
                                        <span className="text-xs text-gray-400 italic">{emptyLabel}</span>
                                      ) : (
                                        currentValues.map((val, idx) => {
                                          const entityKey = typeof val === 'object' ? (val.kpi_key || val.goal_id) : val;
                                          const displayName = resolveEntityTitle(entityKey, refEntityType);
                                          return (
                                            <span 
                                              key={idx}
                                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${tagColorClass}`}
                                            >
                                              {displayName}
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const newValues = currentValues.filter((_, i) => i !== idx);
                                                  setEditValue(newValues);
                                                }}
                                                className="ml-0.5 hover:text-red-500 transition-colors"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </span>
                                          );
                                        })
                                      )}
                                    </div>
                                    <div className="flex justify-end gap-1">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 px-2 text-xs"
                                        onClick={() => {
                                          onSaveEdit?.(actionIdx, fieldName, editValue);
                                        }}
                                      >
                                        <Check className="w-3 h-3 mr-1 text-emerald-600" />
                                        Save
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 px-2 text-xs"
                                        onClick={onCancelEditing}
                                      >
                                        <X className="w-3 h-3 mr-1 text-gray-400" />
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Special case for kpi_key (single KPI reference) - use scoped KPI dropdown
                              if (fieldKey === 'kpi_key') {
                                // Determine parent entity context for scoped KPI fetch
                                const parentEntityId = action.parent_id || action.item_id;
                                const parentEntityType = action.parent_entity_type || action.entity_type || 'feature';
                                
                                // Trigger scoped KPI fetch if needed
                                const scopedData = scopedKpis?.[parentEntityId];
                                if (!scopedData?.fetched && !scopedData?.loading && onFetchScopedKpis && parentEntityId) {
                                  onFetchScopedKpis(parentEntityType, parentEntityId);
                                }
                                
                                // Get options (scoped if available, fallback to all)
                                const kpiOptionsData = getScopedKpiOptions?.(parentEntityType, parentEntityId) || { grouped: false, options: [] };
                                
                                return (
                                  <div className="flex items-center gap-2 w-full">
                                    <Select
                                      value={editValue || ''}
                                      onValueChange={(val) => {
                                        setEditValue(val);
                                        onSaveEdit?.(actionIdx, fieldName, val);
                                        onCancelEditing?.();
                                      }}
                                    >
                                      <SelectTrigger className="flex-1 text-sm h-8">
                                        <SelectValue placeholder={scopedData?.loading ? "Loading KPIs..." : "Select KPI..."}>
                                          {editValue && (() => {
                                            // Format selected value as breadcrumb
                                            const resolved = resolveEntityTitle(editValue, 'kpi', { breadcrumb: true });
                                            return typeof resolved === 'object' 
                                              ? resolved.full 
                                              : resolved;
                                          })()}
                                        </SelectValue>
                                      </SelectTrigger>
                                      <SelectContent className="max-h-[300px]">
                                        {kpiOptionsData.grouped ? (
                                          // Grouped by source (Epic or Tag Initiative)
                                          Object.entries(kpiOptionsData.sources).map(([sourceKey, sourceData]) => (
                                            <SelectGroup key={sourceKey}>
                                              <SelectLabel className="text-xs text-gray-500 font-medium px-2 py-1.5">
                                                {sourceKey === 'epic' ? '📦 From Epic' : '🏷️ From Tag Initiative'}: {sourceData.name}
                                              </SelectLabel>
                                              {sourceData.kpis.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value} className="pl-4">
                                                  <span className="flex items-center gap-1">
                                                    <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                    <span className="text-gray-400">/</span>
                                                    <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                                  </span>
                                                </SelectItem>
                                              ))}
                                            </SelectGroup>
                                          ))
                                        ) : (
                                          // Flat list (fallback)
                                          kpiOptionsData.options?.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                              <span className="flex items-center gap-1">
                                                <span className="text-green-600 dark:text-green-400">{opt.goalTitle}</span>
                                                <span className="text-gray-400">/</span>
                                                <span className="text-indigo-600 dark:text-indigo-400">{opt.metricName}</span>
                                              </span>
                                            </SelectItem>
                                          ))
                                        )}
                                        {(!kpiOptionsData.grouped && (!kpiOptionsData.options || kpiOptionsData.options.length === 0)) && (
                                          <div className="px-3 py-2 text-sm text-gray-500 italic">
                                            No KPIs available. Link goals first.
                                          </div>
                                        )}
                                      </SelectContent>
                                    </Select>
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="h-6 w-6 p-0 flex-shrink-0"
                                      onClick={onCancelEditing}
                                    >
                                      <X className="w-3 h-3 text-gray-400" />
                                    </Button>
                                  </div>
                                );
                              }
                              
                              return null;
                            })() || (
                            /* REF_ENTITY ARRAY: Multi-select dropdown for other array fields */
                            isRefEntityArrayField(fieldName) ? (
                              <>
                                <select
                                  multiple
                                  value={Array.isArray(editValue) ? editValue : (editValue ? [editValue] : [])}
                                  onChange={(e) => {
                                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                    setEditValue(selected);
                                  }}
                                  className="flex-1 text-sm min-h-[120px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
                                >
                                  {getRefEntityOptionsLocal(fieldName).map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex flex-col gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => {
                                      // Save as array
                                      const valToSave = Array.isArray(editValue) ? editValue : (editValue ? [editValue] : []);
                                      onSaveEdit?.(actionIdx, fieldName, valToSave);
                                    }}
                                  >
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={onCancelEditing}
                                  >
                                    <X className="w-3 h-3 text-gray-400" />
                                  </Button>
                                </div>
                              </>
                            ) : isRefEntitySingleField(fieldName) ? (
                              /* REF_ENTITY SINGLE: Single-select dropdown (e.g., owner_id) */
                              <>
                                <select
                                  value={editValue || ''}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="flex-1 text-sm h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
                                >
                                  <option value="">-- Select --</option>
                                  {getRefEntityOptionsLocal(fieldName).map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => handleSaveEdit(fieldName)}
                                  >
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={onCancelEditing}
                                  >
                                    <X className="w-3 h-3 text-gray-400" />
                                  </Button>
                                </div>
                              </>
                            ) : strippedFieldName === 'hypothesis' || String(newValue).length > 50 ? (
                              <>
                                <Textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, fieldName)}
                                  className="flex-1 text-sm"
                                  rows={3}
                                  autoFocus
                                />
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => handleSaveEdit(fieldName)}
                                  >
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={onCancelEditing}
                                  >
                                    <X className="w-3 h-3 text-gray-400" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, fieldName)}
                                  className="flex-1 text-sm h-8"
                                  autoFocus
                                />
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => handleSaveEdit(fieldName)}
                                  >
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={onCancelEditing}
                                  >
                                    <X className="w-3 h-3 text-gray-400" />
                                  </Button>
                                </div>
                              </>
                            )
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                              {formatValueWithResolution(newValue, fieldName)}
                            </div>
                            <button
                              onClick={() => handleStartEditing(fieldName, newValue)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-opacity"
                              title="Edit value"
                            >
                              <Pencil className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// HIERARCHICAL ACTION GROUP (for parent-child relationships like Goal + KPIs)
// =============================================================================

const HierarchicalActionGroup = ({
  group,  // { parent: action, children: [actions] }
  selectedActions,
  onToggleAction,
  expandedActions,
  onToggleExpand,
  globalActionIndex,  // Starting index in the flat actions array
  fieldSchemas = {},
  linkableEntities = {},
  // For scoped KPI dropdowns
  scopedKpis = {},
  onFetchScopedKpis,
  getScopedKpiOptions,
}) => {
  const { parent, children } = group;
  const parentConfig = getActionConfig(parent.type);
  const ParentIcon = parentConfig.icon;
  const parentEntityType = parent.entity_type || 'item';
  const parentEntityLabel = parentEntityType.charAt(0).toUpperCase() + parentEntityType.slice(1);
  const childEntityConfig = getEntityConfig(parentEntityType);
  const childLabel = childEntityConfig.childRelationLabel || 'Items';
  
  // Get child entity type from first child (they should all be the same type)
  const childEntityType = children[0]?.entity_type || 'item';
  const childEntityLabel = childEntityType.charAt(0).toUpperCase() + childEntityType.slice(1);
  
  // Get field schemas for parent and child entity types
  const parentFieldSchema = fieldSchemas[parentEntityType] || {};
  const childFieldSchema = fieldSchemas[childEntityType] || {};
  
  // Format value with entity ID resolution using shared utility
  // For KPIs: returns breadcrumb format "Goal Name / KPI Name"
  const formatValueWithResolution = (value, fieldName, entitySchema) => {
    const fieldKey = fieldName.split('.').pop();
    const fieldConfig = entitySchema[fieldKey];
    const refEntityType = fieldConfig?.ref_entity;
    
    if (!refEntityType) {
      return formatValue(value);
    }
    
    // Special handling for KPI fields - render breadcrumb tags
    if (refEntityType === 'kpi') {
      // Handle arrays of KPI composite keys
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return <span className="text-gray-400 italic">None linked</span>;
        }
        
        return (
          <div className="flex flex-wrap gap-1.5">
            {value.map((id, idx) => {
              const entityKey = typeof id === 'object' ? (id.kpi_key || id) : id;
              const kpis = linkableEntities.kpi || [];
              const kpi = kpis.find(k => k.kpi_key === entityKey);
              
              if (kpi) {
                return (
                  <span key={idx} className="inline-flex items-center gap-0 text-xs" title={entityKey}>
                    <span className="px-1.5 py-0.5 rounded-l bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      {kpi.goal_title || 'Unknown Goal'}
                    </span>
                    <span className="px-1.5 py-0.5 rounded-r bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                      {kpi.metric_name || 'Unnamed KPI'}
                    </span>
                  </span>
                );
              }
              
              return (
                <span key={idx} className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 text-xs">
                  {entityKey}
                </span>
              );
            })}
          </div>
        );
      }
      
      // Handle single KPI composite key
      if (typeof value === 'string' && isCompositeKpiKey(value)) {
        const kpis = linkableEntities.kpi || [];
        const kpi = kpis.find(k => k.kpi_key === value);
        
        if (kpi) {
          return (
            <span className="inline-flex items-center gap-0 text-xs" title={value}>
              <span className="px-1.5 py-0.5 rounded-l bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {kpi.goal_title || 'Unknown Goal'}
              </span>
              <span className="px-1.5 py-0.5 rounded-r bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {kpi.metric_name || 'Unnamed KPI'}
              </span>
            </span>
          );
        }
        
        // Parse and display even if not found in linkableEntities
        const parsed = parseCompositeKpiKey(value);
        if (parsed) {
          return (
            <span className="inline-flex items-center gap-0 text-xs" title={value}>
              <span className="px-1.5 py-0.5 rounded-l bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {parsed.goalId}
              </span>
              <span className="px-1.5 py-0.5 rounded-r bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500">
                {parsed.kpiId}
              </span>
            </span>
          );
        }
      }
    }
    
    // Handle arrays of entity IDs (non-KPI)
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">None linked</span>;
      }
      
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((id, idx) => {
            const title = resolveEntityTitleUtil(id, refEntityType, linkableEntities);
            const isResolved = title !== id;
            return (
              <span 
                key={idx}
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs",
                  isResolved 
                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                )}
                title={id}
              >
                {isResolved ? title : id}
              </span>
            );
          })}
        </div>
      );
    }
    
    // Handle single entity ID
    if (typeof value === 'string' && value.includes('_')) {
      const title = resolveEntityTitleUtil(value, refEntityType, linkableEntities);
      return title !== value ? (
        <span title={value}>{title}</span>
      ) : (
        <span className="text-gray-500">{value}</span>
      );
    }
    
    return formatValue(value);
  };
  
  // Track selection state for parent and children
  const parentIdx = globalActionIndex;
  const isParentSelected = !!selectedActions[parentIdx];
  const isParentExpanded = !!expandedActions[parentIdx];
  
  // Count selected children
  const selectedChildCount = children.filter((_, i) => 
    selectedActions[globalActionIndex + 1 + i]
  ).length;
  const allChildrenSelected = selectedChildCount === children.length;
  
  return (
    <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-800 border-indigo-200 dark:border-indigo-700">
      {/* Parent Item Header */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          parentConfig.bgColor,
          isParentSelected ? "" : "opacity-50"
        )}
        onClick={() => onToggleExpand(parentIdx)}
      >
        <Checkbox
          checked={isParentSelected}
          onCheckedChange={() => onToggleAction(parentIdx)}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0"
        />
        
        <Badge variant="outline" className={cn("text-xs flex items-center gap-1 flex-shrink-0", parentConfig.badgeClass)}>
          <ParentIcon className="w-3 h-3" />
          Create {parentEntityLabel}
        </Badge>
        
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">
            {getActionTitle(parent)}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {parent.fields?.length || 0} fields
            {children.length > 0 && (
              <span className="ml-2 text-indigo-600 dark:text-indigo-400">
                + {children.length} {childLabel}
              </span>
            )}
          </span>
        </div>
        
        <button
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(parentIdx);
          }}
        >
          {isParentExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>
      
      {/* Parent Expanded Details */}
      {isParentExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-850">
          <div className="p-3 space-y-2">
            {(parent.fields || []).map((field, idx) => (
              <div 
                key={idx} 
                className="py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {field.label || getFieldLabel(parent.entity_type, field.field)}
                  </span>
                </div>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {formatValueWithResolution(field.value, field.field, parentFieldSchema)}
                </div>
              </div>
            ))}
          </div>
          
          {/* Children Section */}
          {children.length > 0 && (
            <div className="border-t border-indigo-100 dark:border-indigo-900">
              {/* Children Header */}
              <div className="px-3 py-2 bg-indigo-50/50 dark:bg-indigo-900/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                    {children.length} {childLabel} for this {parentEntityLabel}
                  </span>
                </div>
                <span className="text-xs text-indigo-600 dark:text-indigo-400">
                  {selectedChildCount} of {children.length} selected
                </span>
              </div>
              
              {/* Children List */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {children.map((child, childIdx) => {
                  const childGlobalIdx = globalActionIndex + 1 + childIdx;
                  const isChildSelected = !!selectedActions[childGlobalIdx];
                  const isChildExpanded = !!expandedActions[childGlobalIdx];
                  const childConfig = getActionConfig(child.type);
                  
                  return (
                    <div 
                      key={childIdx}
                      className={cn(
                        "ml-4 border-l-2 border-indigo-200 dark:border-indigo-700",
                        isChildSelected ? "" : "opacity-50"
                      )}
                    >
                      {/* Child Header */}
                      <div 
                        className="flex items-center gap-3 p-2 pl-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        onClick={() => onToggleExpand(childGlobalIdx)}
                      >
                        <Checkbox
                          checked={isChildSelected}
                          onCheckedChange={() => onToggleAction(childGlobalIdx)}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0"
                        />
                        
                        <Badge variant="outline" className={cn("text-xs flex items-center gap-1 flex-shrink-0", childConfig.badgeClass)}>
                          <Plus className="w-3 h-3" />
                          {childEntityLabel}
                        </Badge>
                        
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">
                            {getActionTitle(child)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {child.fields?.length || 0} fields
                          </span>
                        </div>
                        
                        <button
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(childGlobalIdx);
                          }}
                        >
                          {isChildExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                      
                      {/* Child Expanded Details */}
                      {isChildExpanded && (
                        <div className="pl-6 pr-3 pb-3 space-y-2">
                          {(child.fields || []).map((field, fieldIdx) => (
                            <div 
                              key={fieldIdx} 
                              className="py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {field.label}
                                </span>
                              </div>
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {formatValueWithResolution(field.value, field.field, childFieldSchema)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const UnifiedActionReview = ({
  actions: actionsProp = [],
  action: singleAction,  // Backward compat: single action object
  productId,
  contextType,
  contextId,
  onSuccess,
  onActionComplete,  // Backward compat: alias for onSuccess
  onDismiss,
  onSplitItem,  // Special handler for SPLIT_ITEM - uses dedicated modal
  onApplyUpdates,  // For item co-pilots: apply updates to form instead of API
  variant = 'collection',  // 'item' or 'collection' - determines update behavior
  user,
  logActionOutcome,
  relatedData = {},  // Data from parent for merge/split modals (features, epics, personas, etc.)
  className,  // Backward compat: optional styling
  parentZIndex = 50,  // Z-index of parent modal for proper stacking
}) => {
  // Calculate z-index for Review Modal (parent + 10)
  const reviewModalZIndex = parentZIndex + 10;
  
  // Support both single action and actions array
  const rawActions = singleAction 
    ? (singleAction.allActions || [singleAction])
    : actionsProp;
  
  // ==========================================================================
  // NORMALIZE BULK ACTIONS using prescriptive entity config
  // ==========================================================================
  // Supports multiple payload formats:
  // - Format A: action.entity_type + flat item fields (item.title)
  // - Format B: item.entity_type + nested item.fields object
  // - Format C: item.__entity_type + flat item fields (double underscore)
  //
  // Also builds relationship groups for parent-child display (e.g., Goal with KPIs)
  // ==========================================================================
  
  const { actions, actionGroups } = useMemo(() => {
    const normalized = [];
    const groups = []; // For hierarchical display: [{parent: action, children: [actions]}]
    
    for (const action of rawActions) {
      if (action.type === 'BULK_CREATE' && Array.isArray(action.items)) {
        // Track the root item and its dependents for grouping
        let rootAction = null;
        let childActions = [];
        
        action.items.forEach((item, idx) => {
          // Detect entity type from multiple possible locations
          const itemEntityType = getItemEntityType(item, action.entity_type);
          
          // Use prescriptive entity config to extract fields properly
          const extractedFields = extractDisplayFields(item, itemEntityType);
          
          // Get display title using entity config
          const displayTitle = getItemDisplayTitle(item, itemEntityType);
          
          // Determine if this item depends on another item being created
          const dependsOnPrevious = item.parent_id === '__PREVIOUS_ITEM_ID__';
          const hasSyntheticParent = item.parent_id && item.parent_id.startsWith('new_');
          
          const normalizedAction = {
            type: 'CREATE_ITEM',
            entity_type: itemEntityType,
            parent_id: item.parent_id,
            synthetic_id: item.synthetic_id,
            fields: extractedFields,
            _displayTitle: displayTitle,
            _bulkSource: { type: 'BULK_CREATE', index: idx, originalAction: action },
            _dependsOnPrevious: dependsOnPrevious || hasSyntheticParent,
            _originalItem: item, // Keep original for execution
          };
          
          normalized.push(normalizedAction);
          
          // Build relationship groups
          if (!dependsOnPrevious && !hasSyntheticParent) {
            // This is a root item - start a new group
            if (rootAction) {
              // Save previous group if exists
              groups.push({ parent: rootAction, children: childActions });
            }
            rootAction = normalizedAction;
            childActions = [];
          } else {
            // This is a child item
            childActions.push(normalizedAction);
          }
        });
        
        // Don't forget the last group
        if (rootAction) {
          groups.push({ parent: rootAction, children: childActions });
        }
        
      } else if (action.type === 'BULK_UPDATE' && Array.isArray(action.items)) {
        // Expand BULK_UPDATE into individual UPDATE_ITEM actions
        action.items.forEach((item, idx) => {
          const itemEntityType = getItemEntityType(item, action.entity_type);
          const extractedFields = extractDisplayFields(item, itemEntityType);
          const displayTitle = getItemDisplayTitle(item, itemEntityType);
          
          normalized.push({
            type: 'UPDATE_ITEM',
            entity_type: itemEntityType,
            item_id: item.item_id,
            title: item.title,  // Include title for display
            updates: item.updates,  // Preserve updates dict for execution
            fields: extractedFields, // For display only
            reason: action.reason,  // Copy reason from parent BULK_UPDATE
            _displayTitle: displayTitle || item.title,  // Use item.title as fallback
            _bulkSource: { type: 'BULK_UPDATE', index: idx, originalAction: action },
            _originalItem: item,
          });
        });
      } else if (action.type === 'UPDATE_ITEM' || action.type === 'DELETE_ITEM') {
        // For raw UPDATE_ITEM/DELETE_ITEM, pre-compute _displayTitle as fallback
        // This helps when the entity can't be fetched from DB (deleted, wrong ID, etc.)
        const entityType = action.entity_type || getEntityTypeFromId(action.item_id);
        let displayTitle = null;
        
        if (entityType) {
          // Try to get title from action payload (AI may include it)
          displayTitle = getItemDisplayTitle(action, entityType);
          
          // Also check namespaced fields (e.g., persona.name, feature.title)
          if (!displayTitle || displayTitle.startsWith('New ')) {
            const config = getEntityConfig(entityType);
            const namespacedTitle = action[`${entityType}.${config.primaryField}`];
            const namespacedName = action[`${entityType}.name`];
            const namespacedText = action[`${entityType}.title`];
            
            if (namespacedTitle) {
              displayTitle = String(namespacedTitle).substring(0, 60);
            } else if (namespacedName) {
              displayTitle = String(namespacedName).substring(0, 60);
            } else if (namespacedText) {
              displayTitle = String(namespacedText).substring(0, 60);
            }
          }
        }
        
        // Only set _displayTitle if we found a meaningful value
        if (displayTitle && !displayTitle.startsWith('New ')) {
          normalized.push({ ...action, _displayTitle: displayTitle });
        } else {
          normalized.push(action);
        }
      } else {
        // Pass through other action types as-is
        normalized.push(action);
      }
    }
    
    // Helper function for entity type derivation (defined inline for useMemo scope)
    function getEntityTypeFromId(itemId) {
      if (!itemId) return null;
      const prefixMap = {
        'prod_': 'product',
        'product_': 'product',
        'feat_': 'feature',
        'feature_': 'feature',
        'epic_': 'epic',
        'goal_': 'goal',
        'initiative_': 'initiative',
        'persona_': 'persona',
        'request_': 'request',
        'req_': 'request',
      };
      for (const [prefix, type] of Object.entries(prefixMap)) {
        if (itemId.startsWith(prefix)) return type;
      }
      return null;
    }
    
    return { actions: normalized, actionGroups: groups };
  }, [rawActions]);
  
  // Check if we have hierarchical groups to display
  const hasHierarchicalGroups = actionGroups.length > 0 && actionGroups.some(g => g.children.length > 0);
  
  // Support both callback names
  const handleSuccess = onSuccess || onActionComplete;
  
  // Determine if we should use local form updates (item co-pilots) vs API (collection co-pilots)
  const useLocalUpdates = variant === 'item' && onApplyUpdates;
  
  // State
  const [modalOpen, setModalOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState(null);
  
  // Determine display mode (use rawActions to detect BULK)
  const displayMode = useMemo(() => getDisplayMode(rawActions), [rawActions]);
  
  // Create stable keys for resetting state when actions change
  const actionsKey = useMemo(() => 
    actions.map(a => `${a.type}-${a.item_id || ''}-${a.entity_type || ''}`).join('|'),
    [actions]
  );
  
  // Initialize selection state based on current actions (all selected by default)
  const [selectedActions, setSelectedActions] = useState(() => {
    const initial = {};
    actions.forEach((_, idx) => { initial[idx] = true; });
    return initial;
  });
  
  const [expandedActions, setExpandedActions] = useState(() => {
    const expanded = {};
    actions.forEach((_, idx) => { expanded[idx] = idx < 3; });
    return expanded;
  });
  
  // Reset state when actions change
  useEffect(() => {
    // Reset to all selected when actions change
    // Only initialize state for actionable items (exclude OUTCOME and MERGE_ITEMS)
    const initial = {};
    const expanded = {};
    actions.forEach((action, idx) => {
      if (action.type === 'OUTCOME' || action.type === 'MERGE_ITEMS') {
        // Don't initialize selection/expansion for non-actionable items
        return;
      }
      initial[idx] = true;
      expanded[idx] = idx < 3;
    });
    setSelectedActions(initial);
    setExpandedActions(expanded);
    setExecutionResults(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsKey]);
  
  // ==========================================================================
  // ORIGINAL VALUES STATE (for UPDATE comparison)
  // ==========================================================================
  const [originalValues, setOriginalValues] = useState({}); // { item_id: { field: value, ... } }
  const [editedValues, setEditedValues] = useState({}); // { actionIdx: { field: newValue, ... } }
  const [editingField, setEditingField] = useState(null); // { actionIdx, field }
  const [fieldSchemas, setFieldSchemas] = useState({}); // { entity_type: { field: schema, ... } }
  
  // ==========================================================================
  // LINKABLE ENTITIES STATE (for reference field dropdowns)
  // ==========================================================================
  // Stores entities available for linking, keyed by entity type
  // e.g., { goal: [{ goal_id: 'goal_xxx', title: 'Goal Title' }, ...] }
  const [linkableEntities, setLinkableEntities] = useState({});
  
  // ==========================================================================
  // SCOPED KPI STATE (for context-aware KPI dropdowns)
  // ==========================================================================
  // KPIs are scoped to the parent entity chain (Feature→Epic→Initiative→Goals)
  // This stores inherited KPIs for each parent entity we've fetched
  // Format: { 'feat_xxx': { inherited_kpis: [...], loading: false } }
  const [scopedKpis, setScopedKpis] = useState({});
  
  /**
   * Fetch scoped/inherited KPIs for a specific entity
   * Uses the entity's inherited-kpis endpoint to get only relevant KPIs
   * 
   * @param {string} entityType - Type of entity ('feature', 'epic', 'initiative')
   * @param {string} entityId - ID of the entity (e.g., 'feat_xxx')
   */
  const fetchScopedKpis = useCallback(async (entityType, entityId) => {
    if (!entityId) return;
    
    // Check if we already have this entity's KPIs cached
    if (scopedKpis[entityId] && !scopedKpis[entityId].loading) {
      return scopedKpis[entityId].inherited_kpis;
    }
    
    // Mark as loading
    setScopedKpis(prev => ({
      ...prev,
      [entityId]: { ...prev[entityId], loading: true }
    }));
    
    try {
      let response;
      switch (entityType) {
        case 'feature':
        case 'kpi_alignment':
          response = await featuresApi.getInheritedKpis(entityId);
          break;
        case 'epic':
          response = await epicsApi.getInheritedKpis(entityId);
          break;
        case 'initiative':
          response = await initiativesApi.getInheritedKpis(entityId);
          break;
        default:
          // For unknown types, return empty - will fall back to all KPIs
          console.warn(`[fetchScopedKpis] No scoped KPI fetch for entity type: ${entityType}`);
          return [];
      }
      
      const inheritedKpis = response?.data?.inherited_kpis || [];
      
      // Cache the result
      setScopedKpis(prev => ({
        ...prev,
        [entityId]: { inherited_kpis: inheritedKpis, loading: false, fetched: true }
      }));
      
      return inheritedKpis;
    } catch (error) {
      console.error(`[fetchScopedKpis] Error fetching KPIs for ${entityType}:${entityId}:`, error);
      setScopedKpis(prev => ({
        ...prev,
        [entityId]: { inherited_kpis: [], loading: false, error: true }
      }));
      return [];
    }
  }, [scopedKpis]);
  
  /**
   * Get KPI options for a dropdown, scoped to the parent entity if available
   * Falls back to all product KPIs if no parent context
   * 
   * @param {string} parentEntityType - Type of parent entity
   * @param {string} parentEntityId - ID of parent entity  
   * @returns {Array} Options formatted for dropdown
   */
  const getScopedKpiOptions = useCallback((parentEntityType, parentEntityId) => {
    // Try to get scoped KPIs first
    const scopedData = scopedKpis[parentEntityId];
    
    if (scopedData?.inherited_kpis?.length > 0) {
      // Group by source for better display
      const bySource = {};
      scopedData.inherited_kpis.forEach(kpi => {
        const source = kpi.source || 'unknown';
        const sourceName = kpi.source_name || 'Unknown Source';
        if (!bySource[source]) {
          bySource[source] = { name: sourceName, kpis: [] };
        }
        bySource[source].kpis.push({
          value: kpi.kpi_key,
          label: `${kpi.goal_title || 'Unknown Goal'} / ${kpi.metric_name || 'Unnamed KPI'}`,
          goalId: kpi.goal_id,
          goalTitle: kpi.goal_title,
          kpiId: kpi.kpi_id,
          metricName: kpi.metric_name,
          isAligned: kpi.is_aligned,
        });
      });
      
      return { grouped: true, sources: bySource };
    }
    
    // Fall back to all KPIs from linkableEntities
    const allKpis = linkableEntities.kpi || [];
    return {
      grouped: false,
      options: allKpis.map(kpi => ({
        value: kpi.kpi_key,
        label: `${kpi.goal_title || 'Unknown Goal'} / ${kpi.metric_name || 'Unnamed KPI'}`,
        goalId: kpi.goal_id,
        goalTitle: kpi.goal_title,
        kpiId: kpi.kpi_id,
        metricName: kpi.metric_name,
      }))
    };
  }, [scopedKpis, linkableEntities]);
  
  // ==========================================================================
  // UNIFIED MODAL DATA LOADING STATE
  // ==========================================================================
  const [modalDataLoading, setModalDataLoading] = useState(false);
  const [modalDataReady, setModalDataReady] = useState(false);
  
  // ==========================================================================
  // MERGE ACTIONS STATE
  // ==========================================================================
  // Each merge action gets its own button that opens MergeConfirmationModal.
  // When completed, logActionOutcome removes only that action from pending_action.actions.
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [activeMergeAction, setActiveMergeAction] = useState(null);
  const [activeMergeIndex, setActiveMergeIndex] = useState(null);
  
  // Separate actions into: outcomes (resolved), merges, splits, and non-merge/split actions
  // Filter out non-renderable actions (like AWAIT_FEEDBACK) from the "others" bucket
  const { outcomes, mergeActions, splitActions, nonMergeActions } = useMemo(() => {
    const resolved = [];
    const merges = [];
    const splits = [];
    const others = [];
    actions.forEach((action, idx) => {
      const type = (action.type || '').toUpperCase();
      if (type === 'OUTCOME') {
        // Resolved action - render as message
        resolved.push({ outcome: action, originalIndex: idx });
      } else if (type === 'MERGE_ITEMS') {
        merges.push({ action, originalIndex: idx });
      } else if (type === 'SPLIT_ITEM') {
        splits.push({ action, originalIndex: idx });
      } else if (!NON_RENDERABLE_ACTIONS.has(type)) {
        // Only include renderable actions in "others"
        others.push({ action, originalIndex: idx });
      }
      // Non-renderable actions (AWAIT_FEEDBACK, CONFIRM_ACTION) are silently skipped
    });
    return { outcomes: resolved, mergeActions: merges, splitActions: splits, nonMergeActions: others };
  }, [actions]);
  
  // Handle opening merge modal for a specific action
  const handleOpenMerge = useCallback((action, index) => {
    setActiveMergeAction(action);
    setActiveMergeIndex(index);
    setMergeModalOpen(true);
  }, []);
  
  // Handle split action - calls onSplitItem with transformed payload
  // Logging is deferred to the SplitFeatureModal (like MergeConfirmationModal pattern)
  const handleOpenSplit = useCallback((action, actionIndex) => {
    if (!onSplitItem) {
      toast.error('Split not available in this context');
      return;
    }
    
    const actionData = action.data || action;
    const entityType = actionData.entity_type || 'feature';
    
    // Extract flat fields into nested objects for the modal
    // Fields come as split1_title, split1_description, split2_title, etc.
    const feature1 = {};
    const feature2 = {};
    
    Object.keys(actionData).forEach(key => {
      if (key.startsWith('split1_')) {
        const fieldName = key.replace('split1_', '');
        feature1[fieldName] = actionData[key];
      } else if (key.startsWith('split2_')) {
        const fieldName = key.replace('split2_', '');
        feature2[fieldName] = actionData[key];
      }
    });
    
    const splitPayload = {
      feature_1: feature1,
      feature_2: feature2,
      source_id: actionData.source_id,
      archive_source: actionData.archive_source ?? true,
      reason: actionData.reason || '',
      // Include logging callback and metadata for deferred logging on success
      // (Same pattern as MergeConfirmationModal)
      _meta: {
        actionIndex,
        entityType,
        logActionOutcome,
        user,
      }
    };
    
    // Do NOT log here - let the modal log on successful completion
    // This allows user to cancel and come back to the action
    onSplitItem(splitPayload);
  }, [onSplitItem, logActionOutcome, user]);
  
  // Handle dismissing a split action
  const handleDismissSplit = useCallback((action, actionIndex) => {
    const entityType = action.entity_type || 'feature';
    logActionOutcome?.('dismissed', `Split ${entityType}`, {
      author_id: user?.user_id,
      author_name: user?.name || user?.email || 'You',
    }, { actionIndex });
  }, [logActionOutcome, user]);
  
  // Handle merge completion - action removal handled by MergeConfirmationModal via logActionOutcome
  const handleMergeComplete = useCallback(async (result) => {
    setMergeModalOpen(false);
    setActiveMergeAction(null);
    setActiveMergeIndex(null);
    await handleSuccess?.();
  }, [handleSuccess]);
  
  // Handle merge error (keep button visible for retry)
  const handleMergeError = useCallback((error) => {
    // Don't close modal on error - user can retry or cancel
    console.error('[UnifiedActionReview] Merge error:', error);
  }, []);
  
  // Handle dismissing a merge action - removes only this action from pending_action.actions
  const handleDismissMerge = useCallback((action, actionIndex) => {
    const entityType = action.entity_type || 'item';
    logActionOutcome?.('dismissed', `Merge ${action.item_ids?.length || 0} ${entityType}s`, {
      author_id: user?.user_id,
      author_name: user?.name || user?.email || 'You',
    }, { actionIndex });
  }, [logActionOutcome, user]);
  
  // Helper: derive entity type from item_id prefix
  const getEntityTypeFromId = (itemId) => {
    if (!itemId) return null;
    const prefixMap = {
      'prod_': 'product',
      'product_': 'product',
      'feat_': 'feature',
      'feature_': 'feature',
      'epic_': 'epic',
      'goal_': 'goal',
      'initiative_': 'initiative',
      'persona_': 'persona',
      'request_': 'request',
      'req_': 'request',
    };
    for (const [prefix, type] of Object.entries(prefixMap)) {
      if (itemId.startsWith(prefix)) return type;
    }
    return null;
  };
  
  // NOTE: normalizeItemId removed - now using normalizeEntityId from entityIdProcessor
  // This provides unified handling of redundant prefixes like "product_prod_xxx" -> "prod_xxx"
  
  // ==========================================================================
  // ORCHESTRATED MODAL DATA LOADING
  // ==========================================================================
  // Single function that loads ALL data needed for the modal before rendering
  // This prevents cascading fetches and race conditions
  
  const loadAllModalData = useCallback(async () => {
    if (modalDataLoading) return;
    
    setModalDataLoading(true);
    setModalDataReady(false);
    
    try {
      // ========== STEP 1: Calculate what we need ==========
      
      // Find all UPDATE and DELETE actions for fetching originals (need titles for both)
      const actionsNeedingOriginals = actions.filter(a => 
        a.type === 'UPDATE_ITEM' || a.type?.startsWith('UPDATE_') ||
        a.type === 'DELETE_ITEM' || a.type?.startsWith('DELETE_') ||
        a.type === 'LINK_ITEMS' || a.type === 'UNLINK_ITEMS'
      );
      
      // Get unique entity types from actions
      const entityTypes = new Set();
      for (const action of actions) {
        if (action.entity_type) {
          entityTypes.add(action.entity_type);
        }
        if (action.item_id) {
          const derivedType = getEntityTypeFromId(action.item_id);
          if (derivedType) entityTypes.add(derivedType);
        }
      }
      
      // ========== STEP 2: Fetch original values first (needed to derive productId) ==========
      
      let fetchedOriginals = {};
      
      if (actionsNeedingOriginals.length > 0) {
        const embeddedItems = [];
        const standardItems = [];
        
        for (const action of actionsNeedingOriginals) {
          // Check for embedded entity actions (UPDATE_REQUIREMENT, DELETE_KPI_ALIGNMENT, etc.)
          const embeddedMatch = action.type?.match(/^(?:UPDATE|DELETE)_([A-Z_]+)$/);
          if (embeddedMatch && embeddedMatch[1] !== 'ITEM' && action.parent_id && action.item_id) {
            const entityType = embeddedMatch[1].toLowerCase();
            embeddedItems.push({
              parent_id: action.parent_id,
              item_id: action.item_id,
              entity_type: entityType
            });
          } else if ((action.type === 'UPDATE_ITEM' || action.type === 'DELETE_ITEM') && action.item_id) {
            let entityType = action.entity_type;
            // Use unified normalizeEntityId to handle redundant prefixes
            const normalizedId = normalizeEntityId(action.item_id);
            const derivedType = getEntityTypeFromId(normalizedId);
            if (derivedType && derivedType !== entityType) {
              entityType = derivedType;
            }
            if (entityType) {
              standardItems.push({
                item_id: normalizedId,
                entity_type: entityType
              });
            }
          } else if ((action.type === 'LINK_ITEMS' || action.type === 'UNLINK_ITEMS') && action.source_id && action.target_id) {
            // Fetch source entity
            if (action.source_type && action.source_id) {
              const normalizedSourceId = normalizeEntityId(action.source_id);
              standardItems.push({
                item_id: normalizedSourceId,
                entity_type: action.source_type
              });
            }
            // Fetch target entity
            if (action.target_type && action.target_id) {
              const normalizedTargetId = normalizeEntityId(action.target_id);
              standardItems.push({
                item_id: normalizedTargetId,
                entity_type: action.target_type
              });
            }
          }
        }
        
        // Fetch in parallel
        const [embeddedResponse, standardResponse] = await Promise.all([
          embeddedItems.length > 0 
            ? collectionActionsApi.getEmbeddedEntitiesBatch(embeddedItems).catch(() => null)
            : Promise.resolve(null),
          standardItems.length > 0 
            ? collectionActionsApi.getStandardEntitiesBatch(standardItems).catch(() => null)
            : Promise.resolve(null)
        ]);
        
        if (embeddedResponse?.data?.results) {
          fetchedOriginals = { ...fetchedOriginals, ...embeddedResponse.data.results };
        }
        if (standardResponse?.data?.results) {
          fetchedOriginals = { ...fetchedOriginals, ...standardResponse.data.results };
        }
      }
      
      // ========== STEP 3: Determine effective productId ==========
      
      let effectiveProductId = productId;
      if (!effectiveProductId) {
        for (const itemData of Object.values(fetchedOriginals)) {
          if (itemData?.product_id) {
            effectiveProductId = itemData.product_id;
            break;
          }
        }
      }
      
      // ========== STEP 4: Fetch field schemas ==========
      
      const schemas = {};
      if (entityTypes.size > 0) {
        const schemaPromises = Array.from(entityTypes).map(async (entityType) => {
          try {
            const response = await collectionActionsApi.getFieldSchema(entityType);
            if (response.data?.fields) {
              return { entityType, fields: response.data.fields };
            }
          } catch (e) {
            console.warn(`[UnifiedActionReview] Could not fetch schema for ${entityType}:`, e);
          }
          return null;
        });
        
        const schemaResults = await Promise.all(schemaPromises);
        schemaResults.forEach(result => {
          if (result) {
            schemas[result.entityType] = result.fields;
          }
        });
      }
      
      // ========== STEP 5: Determine which ref entities we need ==========
      
      const refEntityTypes = new Set();
      for (const entitySchema of Object.values(schemas)) {
        const schemaRefTypes = extractRefEntityTypes(entitySchema);
        schemaRefTypes.forEach(refType => refEntityTypes.add(refType));
      }
      
      // ========== STEP 6: Fetch all linkable entities using shared utility ==========
      
      const linkables = await fetchLinkableEntities(refEntityTypes, effectiveProductId);
      
      // ========== STEP 7: Set all state atomically ==========
      
      setOriginalValues(fetchedOriginals);
      setFieldSchemas(schemas);
      setLinkableEntities(linkables);
      setModalDataReady(true);
      
    } catch (error) {
      console.error('[UnifiedActionReview] Error loading modal data:', error);
      toast.error('Unable to load review data');
      // Still mark as ready so user can see basic info
      setModalDataReady(true);
    } finally {
      setModalDataLoading(false);
    }
  }, [actions, productId, modalDataLoading]);
  
  // Single useEffect to trigger data loading when modal opens
  useEffect(() => {
    if (modalOpen && !modalDataReady && !modalDataLoading) {
      loadAllModalData();
    }
  }, [modalOpen, modalDataReady, modalDataLoading, loadAllModalData]);
  
  // Reset data ready state when modal closes
  useEffect(() => {
    if (!modalOpen) {
      setModalDataReady(false);
      setOriginalValues({});
      setFieldSchemas({});
      setLinkableEntities({});
    }
  }, [modalOpen]);
  
  // Handle inline edit
  const startEditing = (actionIdx, field) => {
    setEditingField({ actionIdx, field });
  };
  
  const cancelEditing = () => {
    setEditingField(null);
  };
  
  const saveEdit = (actionIdx, field, newValue) => {
    setEditedValues(prev => ({
      ...prev,
      [actionIdx]: {
        ...(prev[actionIdx] || {}),
        [field]: newValue
      }
    }));
    setEditingField(null);
  };
  
  // Get the effective value for a field (edited or original)
  const getEffectiveValue = (actionIdx, field, originalValue) => {
    return editedValues[actionIdx]?.[field] ?? originalValue;
  };
  
  // Check if a field has been edited
  const isFieldEdited = (actionIdx, field) => {
    return editedValues[actionIdx]?.hasOwnProperty(field);
  };
  
  // Reset edits when actions change
  useEffect(() => {
    setOriginalValues({});
    setEditedValues({});
    setEditingField(null);
  }, [actionsKey]);
  
  // ==========================================================================
  // ENTITY TITLE RESOLUTION
  // ==========================================================================
  // For UPDATE_ITEM actions, resolve item_id to human-readable title using fetched originalValues
  const getResolvedActionTitle = useCallback((action) => {
    // First try the standard getActionTitle logic
    const standardTitle = getActionTitle(action);
    
    // If it returned a raw item_id (format: "entity: item_xxx"), try to resolve it
    if (action.item_id && standardTitle.includes(action.item_id)) {
      const entityData = originalValues[action.item_id];
      if (entityData) {
        // Try common title fields
        const titleFields = ['title', 'name', 'metric_name', 'text'];
        for (const field of titleFields) {
          if (entityData[field]) {
            const value = String(entityData[field]);
            return value.length > 60 ? value.substring(0, 60) + '...' : value;
          }
        }
      }
    }
    
    return standardTitle;
  }, [originalValues]);
  
  // Computed values - exclude OUTCOMEs, MERGE_ITEMS, and non-renderable actions from counts
  const actionableActions = useMemo(() => 
    actions.filter(a => {
      const type = (a.type || '').toUpperCase();
      return type !== 'OUTCOME' && 
             type !== 'MERGE_ITEMS' && 
             !NON_RENDERABLE_ACTIONS.has(type);
    }), 
    [actions]
  );
  const totalCount = actionableActions.length;
  const selectedCount = actionableActions.filter((_, idx) => {
    // Find original index in actions array
    const originalIdx = actions.findIndex(a => a === actionableActions[idx]);
    return selectedActions[originalIdx];
  }).length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const noneSelected = selectedCount === 0;
  const typeCounts = useMemo(() => getActionTypeCounts(actionableActions), [actionableActions]);
  const entityCounts = useMemo(() => getEntityTypeCounts(actionableActions), [actionableActions]);
  // Show entity-specific labels if we have ANY entity type info (not just for multiple types)
  const hasEntityTypeInfo = Object.keys(entityCounts).length > 0;
  
  // Computed values for non-merge actions (used in review modal)
  // Extract just the actions for counting (nonMergeActions contains {action, originalIndex})
  const nonMergeActionsOnly = useMemo(() => nonMergeActions.map(item => item.action), [nonMergeActions]);
  const nonMergeCount = nonMergeActions.length;
  const nonMergeTypeCounts = useMemo(() => getActionTypeCounts(nonMergeActionsOnly), [nonMergeActionsOnly]);
  const nonMergeEntityCounts = useMemo(() => getEntityTypeCounts(nonMergeActionsOnly), [nonMergeActionsOnly]);
  const hasNonMergeEntityTypeInfo = Object.keys(nonMergeEntityCounts).length > 0;
  
  // Selection counts for non-merge actions only (for Review modal)
  const nonMergeSelectedCount = useMemo(() => {
    return nonMergeActions.filter(({ originalIndex }) => selectedActions[originalIndex]).length;
  }, [nonMergeActions, selectedActions]);
  const nonMergeAllSelected = nonMergeCount > 0 && nonMergeSelectedCount === nonMergeCount;
  const nonMergeNoneSelected = nonMergeSelectedCount === 0;
  
  // Don't render if no actionable items (only OUTCOMEs/MERGEs/SPLITs handled separately or empty)
  if (totalCount === 0 && mergeActions.length === 0 && splitActions.length === 0) {
    // Only show outcomes if we have them
    if (outcomes.length > 0) {
      return (
        <div className="flex flex-wrap gap-2 items-center">
          {outcomes.filter(({ outcome }) => outcome.summary).map(({ outcome, originalIndex }) => (
            <div key={originalIndex} className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-1">
              <Check className="w-3 h-3 text-green-500" />
              <span>{outcome.outcome === 'accepted' ? 'Applied' : 'Skipped'}: {outcome.summary}</span>
              {outcome.author_name && <span className="text-gray-400">- {outcome.author_name}</span>}
            </div>
          ))}
        </div>
      );
    }
    return null;
  }
  if (displayMode === 'none') return null;
  
  // Execute selected actions (for modal mode)
  const executeSelectedActions = async () => {
    // Filter out MERGE_ITEMS, SPLIT_ITEM, and OUTCOME - they have their own handling
    let selected = actions.filter((action, idx) => selectedActions[idx] && action.type !== 'MERGE_ITEMS' && action.type !== 'SPLIT_ITEM' && action.type !== 'OUTCOME');
    if (selected.length === 0) return;
    
    // Apply any user edits to the selected actions
    selected = selected.map((action, originalIdx) => {
      // Find the actual index in the full actions array
      const actionIdx = actions.findIndex(a => a === action);
      const edits = editedValues[actionIdx];
      
      if (!edits || Object.keys(edits).length === 0) {
        return action;
      }
      
      // Apply edits to the action
      const editedAction = { ...action };
      
      // Determine the entity prefix for namespaced fields
      let entityPrefix = action.entity_type ? `${action.entity_type}.` : '';
      const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
      if (embeddedMatch && !action.entity_type) {
        const derivedEntityType = embeddedMatch[2].toLowerCase();
        entityPrefix = `${derivedEntityType}.`;
      }
      
      // Apply edits to namespaced fields
      for (const [field, newValue] of Object.entries(edits)) {
        const namespacedKey = `${entityPrefix}${field}`;
        if (editedAction[namespacedKey] !== undefined) {
          editedAction[namespacedKey] = newValue;
        }
        // Also check for non-namespaced fields (for backwards compatibility)
        if (editedAction.updates?.[field] !== undefined) {
          editedAction.updates[field] = newValue;
        }
        if (editedAction.fields?.[field] !== undefined) {
          editedAction.fields[field] = newValue;
        }
      }
      
      return editedAction;
    });
    
    setIsExecuting(true);
    const results = { success: [], failed: [] };
    
    // For item co-pilots, collect all form-updatable actions to apply locally
    // This includes UPDATE_ITEM, embedded entity actions, and LINK/UNLINK
    if (useLocalUpdates) {
      // Helper to check if action is an embedded entity action for the CURRENT entity
      const isEmbeddedEntityAction = (action) => {
        const match = action.type?.match(/^(CREATE|UPDATE|DELETE)_(REQUIREMENT|KPI_ALIGNMENT|KPI|MARKET_SEGMENT|COMPETITOR)$/);
        return match !== null;
      };
      
      // Helper to check if action is a LINK/UNLINK targeting the current entity's reference fields
      const isLinkAction = (action) => {
        return action.type === 'LINK_ITEMS' || action.type === 'UNLINK_ITEMS';
      };
      
      // Separate actions into form-updatable vs API-required
      const allUpdateActions = selected.filter(a => a.type === 'UPDATE_ITEM');
      const embeddedActions = selected.filter(a => isEmbeddedEntityAction(a));
      const linkActions = selected.filter(a => isLinkAction(a));
      const otherActions = selected.filter(a => 
        a.type !== 'UPDATE_ITEM' && 
        !isEmbeddedEntityAction(a) && 
        !isLinkAction(a)
      );
      
      // Split UPDATE_ITEM into local (same entity type) vs cross-entity (different entity type)
      const localUpdateActions = allUpdateActions.filter(a => 
        !a.entity_type || a.entity_type === contextType
      );
      const crossEntityUpdateActions = allUpdateActions.filter(a => 
        a.entity_type && a.entity_type !== contextType
      );
      
      const combinedUpdates = {};
      
      // Apply same-entity UPDATE_ITEM actions to form locally
      if (localUpdateActions.length > 0) {
        localUpdateActions.forEach(action => {
          // Handle namespaced fields (e.g., "initiative.goal_ids" -> "goal_ids")
          const entityPrefix = action.entity_type ? `${action.entity_type}.` : '';
          
          if (entityPrefix) {
            for (const [key, value] of Object.entries(action)) {
              if (key.startsWith(entityPrefix)) {
                const fieldName = key.slice(entityPrefix.length);
                combinedUpdates[fieldName] = value;
              }
            }
          }
          
          // Also use action.updates (flat dict format) if present
          if (action.updates) {
            Object.assign(combinedUpdates, action.updates);
          }
        });
        localUpdateActions.forEach(a => results.success.push(a));
      }
      
      // Package cross-entity UPDATE_ITEM actions as pending child updates
      if (crossEntityUpdateActions.length > 0) {
        const pendingUpdates = crossEntityUpdateActions.map(action => {
          const entityPrefix = action.entity_type ? `${action.entity_type}.` : '';
          const updates = {};
          
          // Extract namespaced fields
          for (const [key, value] of Object.entries(action)) {
            if (entityPrefix && key.startsWith(entityPrefix)) {
              updates[key.slice(entityPrefix.length)] = value;
            }
          }
          // Also use action.updates if present
          if (action.updates) {
            Object.assign(updates, action.updates);
          }
          
          // Resolve display name from originalValues (fetched entity data)
          let displayName = action.item_id;
          const entityData = originalValues[action.item_id];
          if (entityData) {
            displayName = entityData.title || entityData.name || entityData.metric_name || action.item_id;
          }
          
          return {
            item_id: action.item_id,
            entity_type: action.entity_type,
            display_name: displayName,
            reason: action.reason || '',
            updates,
          };
        });
        
        combinedUpdates._pendingChildUpdates = pendingUpdates;
        crossEntityUpdateActions.forEach(a => results.success.push(a));
      }
      
      // Process embedded entity actions (requirements, kpi_alignments, kpis, market_segments, competitors) as form updates
      if (embeddedActions.length > 0) {
        // Group by entity type for batch processing
        const requirementActions = embeddedActions.filter(a => a.type?.includes('REQUIREMENT'));
        const kpiAlignmentActions = embeddedActions.filter(a => a.type?.includes('KPI_ALIGNMENT'));
        const kpiActions = embeddedActions.filter(a => a.type?.match(/^(CREATE|UPDATE|DELETE)_KPI$/) !== null);
        const marketSegmentActions = embeddedActions.filter(a => a.type?.includes('MARKET_SEGMENT'));
        const competitorActions = embeddedActions.filter(a => a.type?.includes('COMPETITOR'));
        
        // Process requirement actions
        if (requirementActions.length > 0) {
          // We need to signal to handleApplyUpdates what operations to perform
          // For CREATE: append new requirements
          // For UPDATE: modify existing requirement by item_id
          // For DELETE: remove requirement by item_id
          
          const createReqs = requirementActions.filter(a => a.type === 'CREATE_REQUIREMENT');
          const updateReqs = requirementActions.filter(a => a.type === 'UPDATE_REQUIREMENT');
          const deleteReqs = requirementActions.filter(a => a.type === 'DELETE_REQUIREMENT');
          
          // For CREATE_REQUIREMENT, extract the text and add to requirements array
          if (createReqs.length > 0) {
            const newRequirements = createReqs.map(action => {
              // Extract from namespaced field (requirement.text)
              const text = action['requirement.text'] || action.text || '';
              const isCompleted = action['requirement.is_completed'] || action.is_completed || false;
              return { text, is_completed: isCompleted };
            }).filter(r => r.text);
            
            if (newRequirements.length > 0) {
              combinedUpdates.requirements = newRequirements;
            }
            createReqs.forEach(a => results.success.push(a));
          }
          
          // For UPDATE_REQUIREMENT and DELETE_REQUIREMENT, we need special handling
          // These require the current form state, so we pass operation instructions
          if (updateReqs.length > 0) {
            combinedUpdates._updateRequirements = updateReqs.map(action => ({
              item_id: action.item_id,
              text: action['requirement.text'] || action.text,
              is_completed: action['requirement.is_completed'] ?? action.is_completed
            }));
            updateReqs.forEach(a => results.success.push(a));
          }
          
          if (deleteReqs.length > 0) {
            combinedUpdates._deleteRequirements = deleteReqs.map(action => action.item_id);
            deleteReqs.forEach(a => results.success.push(a));
          }
        }
        
        // Process KPI alignment actions (for features)
        if (kpiAlignmentActions.length > 0) {
          const createKpis = kpiAlignmentActions.filter(a => a.type === 'CREATE_KPI_ALIGNMENT');
          const updateKpis = kpiAlignmentActions.filter(a => a.type === 'UPDATE_KPI_ALIGNMENT');
          const deleteKpis = kpiAlignmentActions.filter(a => a.type === 'DELETE_KPI_ALIGNMENT');
          
          if (createKpis.length > 0) {
            const newKpiAlignments = createKpis.map(action => ({
              kpi_key: action['kpi_alignment.kpi_key'],
              hypothesis: action['kpi_alignment.hypothesis'] || ''
            })).filter(k => k.kpi_key);
            
            if (newKpiAlignments.length > 0) {
              combinedUpdates.kpi_alignments = newKpiAlignments;
            }
            createKpis.forEach(a => results.success.push(a));
          }
          
          if (updateKpis.length > 0) {
            combinedUpdates._updateKpiAlignments = updateKpis.map(action => ({
              kpi_key: action['kpi_alignment.kpi_key'],
              hypothesis: action['kpi_alignment.hypothesis']
            }));
            updateKpis.forEach(a => results.success.push(a));
          }
          
          if (deleteKpis.length > 0) {
            combinedUpdates._deleteKpiAlignments = deleteKpis.map(action => action.item_id);
            deleteKpis.forEach(a => results.success.push(a));
          }
        }
        
        // Process KPI actions (for goals - full KPI objects, not alignments)
        if (kpiActions.length > 0) {
          const createKpis = kpiActions.filter(a => a.type === 'CREATE_KPI');
          const updateKpis = kpiActions.filter(a => a.type === 'UPDATE_KPI');
          const deleteKpis = kpiActions.filter(a => a.type === 'DELETE_KPI');
          
          if (createKpis.length > 0) {
            const newKpis = createKpis.map(action => ({
              metric_name: action['kpi.metric_name'] || action.metric_name || '',
              metric_format: action['kpi.metric_format'] || action.metric_format || 'number',
              start_value: action['kpi.start_value'] ?? action.start_value ?? 0,
              target_value: action['kpi.target_value'] ?? action.target_value ?? 0,
              current_value: action['kpi.current_value'] ?? action.current_value ?? 0,
              confidence: action['kpi.confidence'] || action.confidence || 'medium'
            })).filter(k => k.metric_name);
            
            if (newKpis.length > 0) {
              combinedUpdates.kpis = newKpis;
            }
            createKpis.forEach(a => results.success.push(a));
          }
          
          if (updateKpis.length > 0) {
            combinedUpdates._updateKpis = updateKpis.map(action => ({
              item_id: action.item_id,
              metric_name: action['kpi.metric_name'] ?? action.metric_name,
              metric_format: action['kpi.metric_format'] ?? action.metric_format,
              start_value: action['kpi.start_value'] ?? action.start_value,
              target_value: action['kpi.target_value'] ?? action.target_value,
              current_value: action['kpi.current_value'] ?? action.current_value,
              confidence: action['kpi.confidence'] ?? action.confidence
            }));
            updateKpis.forEach(a => results.success.push(a));
          }
          
          if (deleteKpis.length > 0) {
            combinedUpdates._deleteKpis = deleteKpis.map(action => action.item_id);
            deleteKpis.forEach(a => results.success.push(a));
          }
        }
        
        // Process market segment actions (for products)
        if (marketSegmentActions.length > 0) {
          const createSegments = marketSegmentActions.filter(a => a.type === 'CREATE_MARKET_SEGMENT');
          const updateSegments = marketSegmentActions.filter(a => a.type === 'UPDATE_MARKET_SEGMENT');
          const deleteSegments = marketSegmentActions.filter(a => a.type === 'DELETE_MARKET_SEGMENT');
          
          if (createSegments.length > 0) {
            const newSegments = createSegments.map(action => ({
              name: action['market_segment.name'] || action.name || '',
              description: action['market_segment.description'] || action.description || '',
              size_estimate: action['market_segment.size_estimate'] || action.size_estimate || ''
            })).filter(s => s.name);
            
            if (newSegments.length > 0) {
              combinedUpdates.market_segments = newSegments;
            }
            createSegments.forEach(a => results.success.push(a));
          }
          
          if (updateSegments.length > 0) {
            combinedUpdates._updateMarketSegments = updateSegments.map(action => ({
              item_id: action.item_id,
              name: action['market_segment.name'] ?? action.name,
              description: action['market_segment.description'] ?? action.description,
              size_estimate: action['market_segment.size_estimate'] ?? action.size_estimate
            }));
            updateSegments.forEach(a => results.success.push(a));
          }
          
          if (deleteSegments.length > 0) {
            combinedUpdates._deleteMarketSegments = deleteSegments.map(action => action.item_id);
            deleteSegments.forEach(a => results.success.push(a));
          }
        }
        
        // Process competitor actions (for products)
        if (competitorActions.length > 0) {
          const createCompetitors = competitorActions.filter(a => a.type === 'CREATE_COMPETITOR');
          const updateCompetitors = competitorActions.filter(a => a.type === 'UPDATE_COMPETITOR');
          const deleteCompetitors = competitorActions.filter(a => a.type === 'DELETE_COMPETITOR');
          
          if (createCompetitors.length > 0) {
            const newCompetitors = createCompetitors.map(action => ({
              name: action['competitor.name'] || action.name || '',
              description: action['competitor.description'] || action.description || '',
              strengths: action['competitor.strengths'] || action.strengths || '',
              weaknesses: action['competitor.weaknesses'] || action.weaknesses || ''
            })).filter(c => c.name);
            
            if (newCompetitors.length > 0) {
              combinedUpdates.key_competitors = newCompetitors;
            }
            createCompetitors.forEach(a => results.success.push(a));
          }
          
          if (updateCompetitors.length > 0) {
            combinedUpdates._updateCompetitors = updateCompetitors.map(action => ({
              item_id: action.item_id,
              name: action['competitor.name'] ?? action.name,
              description: action['competitor.description'] ?? action.description,
              strengths: action['competitor.strengths'] ?? action.strengths,
              weaknesses: action['competitor.weaknesses'] ?? action.weaknesses
            }));
            updateCompetitors.forEach(a => results.success.push(a));
          }
          
          if (deleteCompetitors.length > 0) {
            combinedUpdates._deleteCompetitors = deleteCompetitors.map(action => action.item_id);
            deleteCompetitors.forEach(a => results.success.push(a));
          }
        }
      }
      
      // Process LINK/UNLINK actions as form updates (for persona_ids, tag_ids, etc.)
      if (linkActions.length > 0) {
        linkActions.forEach(action => {
          const targetType = action.target_type;
          const targetId = action.target_id;
          const sourceType = action.source_type;
          const sourceId = action.source_id;
          
          // Map target type to form field (standard case: linking TO feature/epic)
          const fieldMap = {
            'persona': 'persona_ids',
            'tag': 'tag_ids',
            'epic': 'epic_id',
            'release': 'release_id'
          };
          
          // SPECIAL CASE: When linking TO a persona (from feature/epic modal context),
          // we need to record the SOURCE as the thing to link
          // e.g., "epic → persona" means add persona_id to epic.persona_ids
          // But from persona modal context, we need to track the epic_id to link
          if (targetType === 'persona' && sourceType && sourceId) {
            // Reverse mapping: source is what we're linking FROM
            const reverseFieldMap = {
              'feature': 'feature_ids',
              'epic': 'epic_ids'
            };
            const reverseFieldName = reverseFieldMap[sourceType];
            
            if (reverseFieldName) {
              if (action.type === 'LINK_ITEMS') {
                combinedUpdates[`_link_${reverseFieldName}`] = combinedUpdates[`_link_${reverseFieldName}`] || [];
                combinedUpdates[`_link_${reverseFieldName}`].push(sourceId);
                results.success.push(action);
              } else if (action.type === 'UNLINK_ITEMS') {
                combinedUpdates[`_unlink_${reverseFieldName}`] = combinedUpdates[`_unlink_${reverseFieldName}`] || [];
                combinedUpdates[`_unlink_${reverseFieldName}`].push(sourceId);
                results.success.push(action);
              }
              return; // Handled, skip standard processing
            }
          }
          
          // Standard case: linking TO the target
          const fieldName = fieldMap[targetType];
          if (!fieldName || !targetId) {
            results.failed.push({ action, error: `Cannot map ${targetType} to form field` });
            return;
          }
          
          if (action.type === 'LINK_ITEMS') {
            // For array fields, signal to append
            if (fieldName.endsWith('_ids')) {
              combinedUpdates[`_link_${fieldName}`] = combinedUpdates[`_link_${fieldName}`] || [];
              combinedUpdates[`_link_${fieldName}`].push(targetId);
            } else {
              // For single-value fields (epic_id, release_id), just set
              combinedUpdates[fieldName] = targetId;
            }
            results.success.push(action);
          } else if (action.type === 'UNLINK_ITEMS') {
            if (fieldName.endsWith('_ids')) {
              combinedUpdates[`_unlink_${fieldName}`] = combinedUpdates[`_unlink_${fieldName}`] || [];
              combinedUpdates[`_unlink_${fieldName}`].push(targetId);
            } else {
              // For single-value fields, clear it
              combinedUpdates[fieldName] = '';
            }
            results.success.push(action);
          }
        });
      }
      
      // Apply all collected updates to form
      if (Object.keys(combinedUpdates).length > 0) {
        onApplyUpdates(combinedUpdates);
      }
      
      // If there are no other actions requiring API calls, we're done
      if (otherActions.length === 0) {
        setIsExecuting(false);
        setExecutionResults(results);
        
        // Dismiss deselected actions (with visible summary, excluding merge actions)
        const deselectedActions = actions.filter((action, idx) => 
          !selectedActions[idx] && action.type !== 'OUTCOME' && action.type !== 'MERGE_ITEMS'
        );
        const deselectedCount = deselectedActions.length;
        deselectedActions.forEach((action, i) => {
          const actionIdx = actions.findIndex(a => a === action);
          if (actionIdx >= 0) {
            const isFirst = i === 0;
            logActionOutcome?.('dismissed', isFirst ? `${deselectedCount} ${deselectedCount === 1 ? 'action' : 'actions'} skipped` : '', {
              author_id: user?.user_id,
              author_name: isFirst ? (user?.name || user?.email || 'You') : '',
            }, { actionIndex: actionIdx });
          }
        });
        
        // Mark applied actions as accepted - first gets summary, rest are silent
        const appliedCount = results.success.length;
        results.success.forEach((action, i) => {
          const actionIdx = actions.findIndex(a => a === action);
          if (actionIdx >= 0) {
            const isFirst = i === 0;
            logActionOutcome?.('accepted', isFirst ? `${appliedCount} ${appliedCount === 1 ? 'update' : 'updates'}` : '', {
              author_id: user?.user_id,
              author_name: isFirst ? (user?.name || user?.email || 'You') : '',
            }, { actionIndex: actionIdx });
          }
        });
        
        toast.success('Updates applied to form - click Save to persist');
        setModalOpen(false);
        // NOTE: Do NOT call handleSuccess() here for local-only updates.
        // handleSuccess triggers onRefresh which re-fetches from server,
        // overwriting local form changes that haven't been saved yet.
        // The user must click Save to persist, then the normal save flow handles refresh.
        return;
      }
      
      // Continue with other actions via API...
      // (fall through to process otherActions)
    }
    
    // Track the current parent reference for dependent actions
    // When an item is created WITHOUT __PREVIOUS_ITEM_ID__, it becomes the new reference
    // All subsequent items WITH __PREVIOUS_ITEM_ID__ use that reference until a new "root" item is created
    let currentParentReference = null;
    // Map synthetic IDs (e.g., "new_goal_1") to real IDs after creation
    const syntheticIdMap = {};
    
    // Helper: resolve a parent_id that may be a synthetic ID
    const resolveSyntheticId = (parentId) => {
      if (!parentId) return parentId;
      // If it's in the map, resolve to real ID
      if (syntheticIdMap[parentId]) return syntheticIdMap[parentId];
      // Legacy fallback
      if (parentId === '__PREVIOUS_ITEM_ID__') return currentParentReference;
      // It's already a real ID
      return parentId;
    };
    
    // Helper: check if a parent_id looks like a real entity ID
    const isRealEntityId = (id) => {
      if (!id) return false;
      return /^(goal|prod|epic|feat|feature|init|persona|release|kpi|member|org|user|note)_/.test(id);
    };
    
    // Helper to check if action was handled locally (for item co-pilots)
    const isLocallyHandledAction = (action) => {
      if (!useLocalUpdates) return false;
      
      // UPDATE_ITEM is always handled locally
      if (action.type === 'UPDATE_ITEM') return true;
      
      // Embedded entity actions (requirements, kpi_alignments, kpis, market_segments, competitors)
      const embeddedMatch = action.type?.match(/^(CREATE|UPDATE|DELETE)_(REQUIREMENT|KPI_ALIGNMENT|KPI|MARKET_SEGMENT|COMPETITOR)$/);
      if (embeddedMatch) return true;
      
      // LINK/UNLINK actions
      if (action.type === 'LINK_ITEMS' || action.type === 'UNLINK_ITEMS') return true;
      
      return false;
    };
    
    // Process actions (exclude locally handled actions when in item co-pilot mode)
    const actionsToProcess = selected.filter(a => !isLocallyHandledAction(a));
    
    for (const action of actionsToProcess) {
      try {
        // Resolve parent_id: synthetic IDs, legacy __PREVIOUS_ITEM_ID__, or real IDs
        let resolvedParentId = resolveSyntheticId(action.parent_id);
        const isSyntheticRef = action.parent_id && action.parent_id !== resolvedParentId;
        const usesPlaceholder = action.parent_id === '__PREVIOUS_ITEM_ID__';
        
        // If parent_id looks like a synthetic reference that couldn't be resolved, fail early
        if (action.parent_id && !isRealEntityId(action.parent_id) && action.parent_id !== '__PREVIOUS_ITEM_ID__' && !syntheticIdMap[action.parent_id]) {
          results.failed.push({ 
            action, 
            error: `Cannot create ${action.entity_type || action.type}: parent item "${action.parent_id}" was not created (it may have been deselected)` 
          });
          continue;
        }
        
        if (usesPlaceholder) {
          if (!currentParentReference) {
            results.failed.push({ 
              action, 
              error: `Cannot create ${action.entity_type}: no previous item was created` 
            });
            continue;
          }
          resolvedParentId = currentParentReference;
        }
        
        switch (action.type) {
          case 'UPDATE_ITEM': {
            // Handle both namespaced fields (new schema) and legacy nested fields
            let updatesToSend = action.updates;
            if (!updatesToSend || Object.keys(updatesToSend).length === 0) {
              // Extract namespaced fields from action and strip the prefix
              const systemFields = ['type', 'entity_type', 'item_id', 'reason', 'action_id'];
              updatesToSend = {};
              const entityPrefix = action.entity_type ? `${action.entity_type}.` : '';
              
              for (const [key, value] of Object.entries(action)) {
                if (systemFields.includes(key)) continue;
                if (value === undefined || value === null) continue;
                
                if (key.startsWith(entityPrefix)) {
                  const fieldName = key.slice(entityPrefix.length);
                  updatesToSend[fieldName] = value;
                } else if (!key.includes('.')) {
                  updatesToSend[key] = value;
                }
              }
            }
            
            await collectionActionsApi.updateItem({
              product_id: productId,
              item_id: action.item_id,
              entity_type: action.entity_type,
              updates: updatesToSend,
            }).then(response => {
              // Check for KPI integrity message and show as informational toast
              if (response?.data?.kpi_integrity_message) {
                toast.info(response.data.kpi_integrity_message, {
                  duration: 8000,
                  description: 'KPI subscriptions were updated to maintain hierarchy integrity.'
                });
              }
            });
            results.success.push(action);
            break;
          }
            
          case 'CREATE_ITEM': {
            // Handle both namespaced fields (new schema) and legacy nested fields
            // Namespaced schema: { type, entity_type, parent_id, "request.title", "request.priority", ... }
            // Legacy schema: { type, entity_type, parent_id, fields: { title, description, ... } }
            let fieldsToSend = action.fields;
            if (!fieldsToSend || Object.keys(fieldsToSend).length === 0) {
              // Extract namespaced fields from action and strip the prefix
              const systemFields = ['type', 'entity_type', 'parent_id', 'reason', 'action_id'];
              fieldsToSend = {};
              const entityPrefix = action.entity_type ? `${action.entity_type}.` : '';
              
              for (const [key, value] of Object.entries(action)) {
                if (systemFields.includes(key)) continue;
                if (value === undefined || value === null) continue;
                
                // Check for namespaced field (e.g., "request.title" -> "title")
                if (key.startsWith(entityPrefix)) {
                  const fieldName = key.slice(entityPrefix.length);
                  fieldsToSend[fieldName] = value;
                } else if (!key.includes('.')) {
                  // Non-namespaced field (legacy flat format)
                  fieldsToSend[key] = value;
                }
              }
            }
            
            const createResponse = await collectionActionsApi.createItem({
              product_id: productId,
              entity_type: action.entity_type,
              parent_id: resolvedParentId,
              fields: fieldsToSend,
            });
            results.success.push(action);
            // Track created ID (axios returns { data: {...} })
            const createdItemId = createResponse?.data?.item_id;
            // Store in synthetic ID map if this action had a synthetic_id
            if (createdItemId && action.synthetic_id) {
              syntheticIdMap[action.synthetic_id] = createdItemId;
            }
            // Also update legacy currentParentReference for backwards compat
            if (createdItemId && !usesPlaceholder && !isSyntheticRef) {
              currentParentReference = createdItemId;
            }
            break;
          }
            
          case 'DELETE_ITEM':
            await collectionActionsApi.deleteItem({
              product_id: productId,
              item_id: action.item_id,
              entity_type: action.entity_type,
            });
            results.success.push(action);
            break;
            
          case 'LINK_ITEMS':
            await collectionActionsApi.linkItems({
              product_id: productId,
              source_type: action.source?.type,
              source_id: action.source?.id,
              target_type: action.target?.type,
              target_id: action.target?.id,
            });
            results.success.push(action);
            break;
            
          case 'UNLINK_ITEMS':
            await collectionActionsApi.unlinkItems({
              product_id: productId,
              source_type: action.source?.type,
              source_id: action.source?.id,
              target_type: action.target?.type,
              target_id: action.target?.id,
            });
            results.success.push(action);
            break;
          
          case 'BULK_UPDATE': {
            // Handle namespaced fields in items
            const itemsToUpdate = (action.items || action.data?.items || []).map(item => {
              const entityType = item.entity_type || action.entity_type;
              const entityPrefix = entityType ? `${entityType}.` : '';
              const systemFields = ['item_id', 'entity_type'];
              const updates = {};
              
              for (const [key, value] of Object.entries(item)) {
                if (systemFields.includes(key)) continue;
                if (value === undefined || value === null) continue;
                
                if (key.startsWith(entityPrefix)) {
                  updates[key.slice(entityPrefix.length)] = value;
                } else if (!key.includes('.')) {
                  updates[key] = value;
                }
              }
              
              return {
                item_id: item.item_id,
                updates: Object.keys(updates).length > 0 ? updates : item.updates || item
              };
            });
            
            await collectionActionsApi.bulkUpdate({
              product_id: productId,
              entity_type: action.entity_type,
              items: itemsToUpdate,
            });
            results.success.push(action);
            break;
          }
          
          case 'BULK_CREATE': {
            // Handle namespaced fields in items
            const itemsToCreate = (action.items || action.data?.items || []).map(item => {
              const entityType = item.entity_type || action.entity_type;
              const entityPrefix = entityType ? `${entityType}.` : '';
              const systemFields = ['entity_type', 'parent_id', 'synthetic_id'];
              const fields = {};
              
              for (const [key, value] of Object.entries(item)) {
                if (systemFields.includes(key)) continue;
                if (value === undefined || value === null) continue;
                
                if (key.startsWith(entityPrefix)) {
                  fields[key.slice(entityPrefix.length)] = value;
                } else if (!key.includes('.')) {
                  fields[key] = value;
                }
              }
              
              return {
                entity_type: entityType,
                parent_id: resolveSyntheticId(item.parent_id) || resolvedParentId,
                synthetic_id: item.synthetic_id,
                fields: Object.keys(fields).length > 0 ? fields : item.fields || item
              };
            });
            
            const bulkCreateResult = await collectionActionsApi.bulkCreate({
              product_id: productId,
              entity_type: action.entity_type,
              parent_id: resolvedParentId,
              items: itemsToCreate,
            });
            results.success.push(action);
            // Track created IDs and map synthetic IDs
            if (bulkCreateResult?.data?.created_ids?.length > 0) {
              // Map synthetic IDs from items to real created IDs
              itemsToCreate.forEach((item, idx) => {
                if (item.synthetic_id && bulkCreateResult.data.created_ids[idx]) {
                  syntheticIdMap[item.synthetic_id] = bulkCreateResult.data.created_ids[idx];
                }
              });
              // Legacy: update currentParentReference with first created item
              if (!usesPlaceholder && !isSyntheticRef) {
                currentParentReference = bulkCreateResult.data.created_ids[0];
              }
            }
            break;
          }
          
          case 'BULK_LINK':
            await collectionActionsApi.bulkLink({
              product_id: productId,
              source_type: action.source_type || action.source?.type,
              source_ids: action.source_ids || [],
              target_type: action.target_type || action.target?.type,
              target_id: action.target_id || action.target?.id,
            });
            results.success.push(action);
            break;
          
          case 'MERGE_ITEMS':
            // Handle both flattened (new) and nested (legacy) merged_content formats
            const mergedContent = action.merged_content || {
              title: action.merged_content_title,
              description: action.merged_content_description,
            };
            await collectionActionsApi.mergeItems({
              product_id: productId,
              entity_type: action.entity_type,
              item_ids: action.item_ids || [],
              merged_content: mergedContent,
            });
            results.success.push(action);
            break;
          
          case 'SPLIT_ITEM':
            // SPLIT_ITEM uses a dedicated modal flow via onSplitItem callback
            // Transform flat split1_/split2_ fields → feature_1/feature_2 objects for modal
            if (onSplitItem) {
              const actionData = action.data || action;
              
              // Extract flat fields into nested objects for the modal
              // Fields come as split1_title, split1_description, split2_title, etc.
              const feature1 = {};
              const feature2 = {};
              
              Object.keys(actionData).forEach(key => {
                if (key.startsWith('split1_')) {
                  const fieldName = key.replace('split1_', '');
                  feature1[fieldName] = actionData[key];
                } else if (key.startsWith('split2_')) {
                  const fieldName = key.replace('split2_', '');
                  feature2[fieldName] = actionData[key];
                }
              });
              
              const splitPayload = {
                feature_1: feature1,
                feature_2: feature2,
                source_id: actionData.source_id,
                archive_source: actionData.archive_source ?? true
              };
              onSplitItem(splitPayload);
              results.success.push(action);
            } else {
              results.failed.push({ action, error: 'Split not available in this context' });
            }
            break;
          
          // =====================================================================
          // FIRST-CLASS EMBEDDED ENTITY ACTIONS
          // =====================================================================
          // These handle CREATE/UPDATE/DELETE for array fields treated as entities
          // (e.g., market_segment stored in product.market_segments array)
          // =====================================================================
          
          default: {
            // Check if this is a first-class embedded entity action
            // Pattern: CREATE_MARKET_SEGMENT, UPDATE_MARKET_SEGMENT, DELETE_MARKET_SEGMENT, etc.
            const embeddedMatch = action.type.match(/^(CREATE|UPDATE|DELETE)_([A-Z_]+)$/);
            
            if (embeddedMatch) {
              const [, operation, entityTypeUpper] = embeddedMatch;
              const entityType = entityTypeUpper.toLowerCase(); // e.g., "market_segment"
              const entityPrefix = `${entityType}.`;
              
              // Extract namespaced fields
              const systemFields = ['type', 'parent_id', 'item_id', 'reason', 'action_id', 'entity_type'];
              const fields = {};
              
              for (const [key, value] of Object.entries(action)) {
                if (systemFields.includes(key)) continue;
                if (value === undefined || value === null) continue;
                
                if (key.startsWith(entityPrefix)) {
                  fields[key.slice(entityPrefix.length)] = value;
                }
              }
              
              const basePayload = {
                product_id: productId,
                parent_id: resolvedParentId,
                entity_type: entityType,
              };
              
              switch (operation) {
                case 'CREATE':
                  await collectionActionsApi.createEmbeddedEntity({
                    ...action, // Include all namespaced fields (kpi.metric_name, etc.)
                    ...basePayload, // Resolved parent_id MUST override raw action.parent_id
                  });
                  results.success.push(action);
                  break;
                  
                case 'UPDATE':
                  await collectionActionsApi.updateEmbeddedEntity({
                    ...action, // Include all namespaced fields
                    ...basePayload, // Resolved parent_id overrides
                    item_id: action.item_id,
                  });
                  results.success.push(action);
                  break;
                  
                case 'DELETE':
                  await collectionActionsApi.deleteEmbeddedEntity({
                    ...basePayload,
                    item_id: action.item_id,
                  });
                  results.success.push(action);
                  break;
                  
                default:
                  results.failed.push({ action, error: `Unsupported embedded action: ${action.type}` });
              }
            } else {
              console.warn(`[UnifiedActionReview] Unhandled action type: ${action.type}`);
              results.failed.push({ action, error: `Unsupported action type: ${action.type}` });
            }
          }
        }
      } catch (error) {
        console.error(`[UnifiedActionReview] Action failed:`, action.type, error);
        results.failed.push({ action, error: error.response?.data?.detail || error.message });
      }
    }
    
    setIsExecuting(false);
    setExecutionResults(results);
    
    // Show toast and log to chat
    const successCount = results.success.length;
    const failedCount = results.failed.length;
    
    // Identify deselected actions (not selected, not already resolved, not merge actions)
    const deselectedActions = actions.filter((action, idx) => 
      !selectedActions[idx] && action.type !== 'OUTCOME' && action.type !== 'MERGE_ITEMS'
    );
    
    // Mark deselected actions as dismissed (with visible summary)
    const deselectedCount = deselectedActions.length;
    deselectedActions.forEach((action, i) => {
      const actionIdx = actions.findIndex(a => a === action);
      if (actionIdx >= 0) {
        // First deselected action gets the count summary, rest are silent
        const isFirst = i === 0;
        logActionOutcome?.('dismissed', isFirst ? `${deselectedCount} ${deselectedCount === 1 ? 'action' : 'actions'} skipped` : '', {
          author_id: user?.user_id,
          author_name: isFirst ? (user?.name || user?.email || 'You') : '',
        }, { actionIndex: actionIdx });
      }
    });
    
    if (failedCount === 0) {
      toast.success(`Applied ${successCount} ${successCount === 1 ? 'action' : 'actions'}`);
      // Mark each successful action as accepted - first gets summary, rest are silent
      results.success.forEach((action, i) => {
        const actionIdx = actions.findIndex(a => a === action);
        if (actionIdx >= 0) {
          const isFirst = i === 0;
          logActionOutcome?.('accepted', isFirst ? `${successCount} ${successCount === 1 ? 'action' : 'actions'}` : '', {
            author_id: user?.user_id,
            author_name: isFirst ? (user?.name || user?.email || 'You') : '',
          }, { actionIndex: actionIdx });
        }
      });
      setModalOpen(false);
      await handleSuccess?.();
    } else if (successCount === 0) {
      toast.error(`All ${failedCount} actions failed`);
      // Mark each failed action as dismissed - first gets summary, rest are silent
      results.failed.forEach(({ action }, i) => {
        const actionIdx = actions.findIndex(a => a === action);
        if (actionIdx >= 0) {
          const isFirst = i === 0;
          logActionOutcome?.('dismissed', isFirst ? `${failedCount} ${failedCount === 1 ? 'action' : 'actions'} failed` : '', {
            author_id: user?.user_id,
            author_name: isFirst ? (user?.name || user?.email || 'You') : '',
          }, { actionIndex: actionIdx });
        }
      });
    } else {
      toast.warning(`${successCount} succeeded, ${failedCount} failed`);
      // Mark successful actions as accepted - first gets summary
      results.success.forEach((action, i) => {
        const actionIdx = actions.findIndex(a => a === action);
        if (actionIdx >= 0) {
          const isFirst = i === 0;
          logActionOutcome?.('accepted', isFirst ? `${successCount} ${successCount === 1 ? 'action' : 'actions'}` : '', {
            author_id: user?.user_id,
            author_name: isFirst ? (user?.name || user?.email || 'You') : '',
          }, { actionIndex: actionIdx });
        }
      });
      // Mark failed actions as dismissed - first gets summary
      results.failed.forEach(({ action }, i) => {
        const actionIdx = actions.findIndex(a => a === action);
        if (actionIdx >= 0) {
          const isFirst = i === 0;
          logActionOutcome?.('dismissed', isFirst ? `${failedCount} ${failedCount === 1 ? 'action' : 'actions'} failed` : '', {
            author_id: user?.user_id,
            author_name: isFirst ? (user?.name || user?.email || 'You') : '',
          }, { actionIndex: actionIdx });
        }
      });
      // Partial success - still trigger refresh to show successfully created items
      await handleSuccess?.();
    }
  };
  
  const handleDismiss = () => {
    // Count non-resolved actions
    const unresolvedActions = actions.filter(a => a.type !== 'OUTCOME');
    const count = unresolvedActions.length;
    
    if (count === 0) return;
    
    // Dismiss all at once - mark each action index but show one consolidated message
    // First action gets the summary message, rest are silent updates
    unresolvedActions.forEach((action, i) => {
      const idx = actions.findIndex(a => a === action);
      const isFirst = i === 0;
      logActionOutcome?.('dismissed', isFirst ? `${count} ${count === 1 ? 'action' : 'actions'}` : '', {
        author_id: user?.user_id,
        author_name: isFirst ? (user?.name || user?.email || 'You') : '',
      }, { actionIndex: idx });
    });
    onDismiss?.();
  };
  
  // Handlers for modal
  const toggleAction = (idx) => {
    setSelectedActions(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  
  const toggleSelectAll = () => {
    const newValue = !nonMergeAllSelected;
    const newState = { ...selectedActions };
    // Only toggle actionable items (exclude OUTCOME and MERGE_ITEMS)
    actions.forEach((action, idx) => {
      if (action.type !== 'OUTCOME' && action.type !== 'MERGE_ITEMS') {
        newState[idx] = newValue;
      }
    });
    setSelectedActions(newState);
  };
  
  const toggleExpand = (idx) => {
    setExpandedActions(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  
  const expandAll = () => {
    const newState = { ...expandedActions };
    // Only expand actionable items
    actions.forEach((action, idx) => {
      if (action.type !== 'OUTCOME' && action.type !== 'MERGE_ITEMS') {
        newState[idx] = true;
      }
    });
    setExpandedActions(newState);
  };
  
  const collapseAll = () => {
    const newState = { ...expandedActions };
    // Only collapse actionable items
    actions.forEach((action, idx) => {
      if (action.type !== 'OUTCOME' && action.type !== 'MERGE_ITEMS') {
        newState[idx] = false;
      }
    });
    setExpandedActions(newState);
  };
  
  const anyExpanded = Object.values(expandedActions).some(Boolean);
  
  // ==========================================================================
  // RENDER: MODAL MODE (all actions use review modal)
  // ==========================================================================
  
  return (
    <>
      {/* Container for all action cards */}
      <div className="space-y-2">
        {/* OUTCOME MESSAGES - Resolved actions displayed as messages (only show if has summary) */}
        {outcomes.filter(({ outcome }) => outcome.summary).map(({ outcome, originalIndex }) => {
          const isAccepted = outcome.outcome === 'accepted';
          return (
            <div 
              key={`outcome-${originalIndex}`}
              className={cn(
                "mx-2 my-1 px-3 py-2 rounded-lg text-sm",
                isAccepted 
                  ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                  : "bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400"
              )}
              data-testid={`outcome-${originalIndex}`}
            >
              <span>{isAccepted ? '✅' : '❌'} {isAccepted ? 'Applied' : 'Dismissed'}: {outcome.summary}</span>
              {outcome.author_name && (
                <span className="ml-2 text-xs opacity-60">- {outcome.author_name}</span>
              )}
            </div>
          );
        })}
        
        {/* MERGE ACTION BUTTONS - Each merge gets its own button card */}
        {mergeActions.map(({ action: mergeAction, originalIndex }, idx) => {
          const mergeConfig = getActionConfig('MERGE_ITEMS');
          const entityType = mergeAction.entity_type || 'item';
          const itemCount = mergeAction.item_ids?.length || 0;
          const EntityIcon = mergeConfig.icon;
          
          return (
            <div 
              key={`merge-${idx}`}
              className={cn(
                "mx-2 my-2 px-3 py-2 rounded-lg border",
                "bg-white dark:bg-gray-900 border-indigo-200 dark:border-indigo-800"
              )}
              data-testid={`merge-action-card-${idx}`}
            >
              <div className="flex items-center justify-between gap-2">
                {/* Left side: Icon + description */}
                <div className="flex items-center gap-2 flex-wrap">
                  <EntityIcon className={cn("w-4 h-4 flex-shrink-0", mergeConfig.color)} />
                  <span className={cn("font-medium text-sm", mergeConfig.color)}>
                    Merge Proposed
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <Badge variant="outline" className={cn("text-xs", mergeConfig.badgeClass)}>
                    {itemCount} {entityType.charAt(0).toUpperCase() + entityType.slice(1)}s → 1
                  </Badge>
                </div>
                
                {/* Right side: Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDismissMerge(mergeAction, originalIndex)}
                    className="text-xs h-7 px-2 text-gray-600 hover:text-gray-900"
                    data-testid={`dismiss-merge-btn-${idx}`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleOpenMerge(mergeAction, originalIndex)}
                    className={cn("text-white text-xs h-7 px-3", "bg-indigo-600 hover:bg-indigo-700")}
                    data-testid={`merge-btn-${idx}`}
                  >
                    <GitMerge className="w-3 h-3 mr-1" />
                    Merge
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        
        {/* SPLIT ACTION BUTTONS - Each split gets its own button card */}
        {splitActions.map(({ action: splitAction, originalIndex }, idx) => {
          const splitConfig = getActionConfig('SPLIT_ITEM');
          const entityType = splitAction.entity_type || 'feature';
          const SplitIcon = splitConfig.icon;
          
          return (
            <div 
              key={`split-${idx}`}
              className={cn(
                "mx-2 my-2 px-3 py-2 rounded-lg border",
                "bg-white dark:bg-gray-900 border-orange-200 dark:border-orange-800"
              )}
              data-testid={`split-action-card-${idx}`}
            >
              <div className="flex items-center justify-between gap-2">
                {/* Left side: Icon + description */}
                <div className="flex items-center gap-2 flex-wrap">
                  <SplitIcon className={cn("w-4 h-4 flex-shrink-0", splitConfig.color)} />
                  <span className={cn("font-medium text-sm", splitConfig.color)}>
                    Split Proposed
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <Badge variant="outline" className={cn("text-xs", splitConfig.badgeClass)}>
                    1 {entityType.charAt(0).toUpperCase() + entityType.slice(1)} → 2
                  </Badge>
                </div>
                
                {/* Right side: Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDismissSplit(splitAction, originalIndex)}
                    className="text-xs h-7 px-2 text-gray-600 hover:text-gray-900"
                    data-testid={`dismiss-split-btn-${idx}`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleOpenSplit(splitAction, originalIndex)}
                    className={cn("text-white text-xs h-7 px-3", "bg-orange-600 hover:bg-orange-700")}
                    data-testid={`split-btn-${idx}`}
                  >
                    <Scissors className="w-3 h-3 mr-1" />
                    Split
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        
        {/* NON-MERGE ACTIONS - Use standard review card if any exist */}
        {nonMergeCount > 0 && (
          <div 
            className={cn(
              "mx-2 my-2 px-3 py-2 rounded-lg border",
              "bg-white dark:bg-gray-900 border-indigo-200 dark:border-indigo-800"
            )}
            data-testid="unified-action-review-card"
          >
            <div className="flex items-center justify-between gap-2">
              {/* Left side: Icon + count + type badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="font-medium text-sm text-indigo-600 dark:text-indigo-400">
                  {nonMergeCount} {nonMergeCount === 1 ? 'Action' : 'Actions'} Proposed
                </span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                {/* Type breakdown badges inline */}
                {hasNonMergeEntityTypeInfo ? (
                  Object.entries(nonMergeEntityCounts).map(([key, data]) => {
                    const [verb, ...entityParts] = key.split('_');
                    const entityType = entityParts.join(' ');
                    const config = getActionConfig(data.actionType);
                    const verbLabel = verb.charAt(0).toUpperCase() + verb.slice(1);
                    const entityLabel = entityType.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    return (
                      <Badge key={key} variant="outline" className={cn("text-xs", config.badgeClass)}>
                        {data.count} {verbLabel} {entityLabel}{data.count > 1 && !entityLabel.endsWith('s') ? 's' : ''}
                      </Badge>
                    );
                  })
                ) : (
                  Object.entries(nonMergeTypeCounts).map(([type, count]) => {
                    const config = getActionConfig(type);
                    return (
                      <Badge key={type} variant="outline" className={cn("text-xs", config.badgeClass)}>
                        {count} {config.label}
                      </Badge>
                    );
                  })
                )}
              </div>
              
              {/* Right side: Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  className="text-xs h-7 px-2 text-gray-600 hover:text-gray-900"
                  data-testid="dismiss-actions-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  onClick={() => setModalOpen(true)}
                  className="text-white bg-indigo-600 hover:bg-indigo-700 text-xs h-7 px-3"
                  data-testid="review-actions-btn"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Review
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Review Modal - Custom pattern for proper z-index stacking */}
      {modalOpen && (
        <div className="fixed inset-0" style={{ zIndex: reviewModalZIndex }}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Modal Content */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-[80vw] w-[80vw] max-h-[90vh] flex flex-col overflow-hidden">
              <ModalZIndexProvider zIndex={reviewModalZIndex}>
              {/* Header */}
              <div className="p-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review Proposed Changes</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Select which changes to apply. Review each item before applying.
                </p>
            
                {/* Type badges - show entity breakdown for hierarchical groups */}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {hasNonMergeEntityTypeInfo ? (
                    Object.entries(nonMergeEntityCounts).map(([key, data]) => {
                      const [verb, ...entityParts] = key.split('_');
                      const entityType = entityParts.join(' ');
                      const config = getActionConfig(data.actionType);
                      const verbLabel = verb.charAt(0).toUpperCase() + verb.slice(1);
                      const entityLabel = entityType.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      return (
                        <Badge key={key} variant="outline" className={cn("text-xs", config.badgeClass)}>
                          {data.count} {verbLabel} {entityLabel}{data.count > 1 && !entityLabel.endsWith('s') ? 's' : ''}
                        </Badge>
                      );
                    })
                  ) : (
                    Object.entries(nonMergeTypeCounts).map(([type, count]) => {
                      const config = getActionConfig(type);
                      return (
                        <Badge key={type} variant="outline" className={cn("text-xs", config.badgeClass)}>
                          {count} {config.label}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </div>
          
          {/* Loading State - Show spinner until all data is ready */}
          {!modalDataReady ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading review data...</p>
            </div>
          ) : (
            <>
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={nonMergeAllSelected}
                onCheckedChange={toggleSelectAll}
                className="mr-1"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {nonMergeSelectedCount} of {nonMergeCount} selected
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={anyExpanded ? collapseAll : expandAll}
              className="text-xs h-7"
            >
              {anyExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </div>
          
          {/* Execution Results (if any failures) */}
          {executionResults?.failed?.length > 0 && (
            <div className="mx-4 mt-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium text-sm">{executionResults.failed.length} Failed</span>
              </div>
              <ul className="text-xs text-red-600 dark:text-red-300 space-y-1">
                {executionResults.failed.map((f, idx) => (
                  <li key={idx}>• {getActionTitle(f.action)}: {f.error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Actions list - use hierarchical display when we have parent-child relationships */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[50vh]">
            {hasHierarchicalGroups ? (
              // Render hierarchical groups (e.g., Goal with nested KPIs)
              actionGroups.map((group, groupIdx) => {
                // Calculate the starting index for this group in the flat actions array
                const prevGroupsActionCount = actionGroups
                  .slice(0, groupIdx)
                  .reduce((sum, g) => sum + 1 + g.children.length, 0);
                
                return (
                  <HierarchicalActionGroup
                    key={groupIdx}
                    group={group}
                    selectedActions={selectedActions}
                    onToggleAction={toggleAction}
                    expandedActions={expandedActions}
                    onToggleExpand={toggleExpand}
                    globalActionIndex={prevGroupsActionCount}
                    fieldSchemas={fieldSchemas}
                    linkableEntities={linkableEntities}
                    // For scoped KPI dropdowns
                    scopedKpis={scopedKpis}
                    onFetchScopedKpis={fetchScopedKpis}
                    getScopedKpiOptions={getScopedKpiOptions}
                  />
                );
              })
            ) : (
              // Flat list for non-hierarchical actions (excluding merge actions and outcomes)
              actions
                .map((action, idx) => ({ action, idx }))
                .filter(({ action }) => action.type !== 'MERGE_ITEMS' && action.type !== 'OUTCOME')
                .map(({ action, idx }) => (
                <ActionItem
                  key={idx}
                  action={action}
                  actionIdx={idx}
                  isSelected={!!selectedActions[idx]}
                  onToggle={() => toggleAction(idx)}
                  isExpanded={!!expandedActions[idx]}
                  onToggleExpand={() => toggleExpand(idx)}
                    // For UPDATE comparison and inline editing
                    originalValues={originalValues}
                    editedValues={editedValues}
                    editingField={editingField}
                    onStartEditing={startEditing}
                    onSaveEdit={saveEdit}
                    onCancelEditing={cancelEditing}
                    getEffectiveValue={getEffectiveValue}
                    isFieldEdited={isFieldEdited}
                    fieldSchemas={fieldSchemas}
                    linkableEntities={linkableEntities}
                    // For scoped KPI dropdowns
                    scopedKpis={scopedKpis}
                    onFetchScopedKpis={fetchScopedKpis}
                    getScopedKpiOptions={getScopedKpiOptions}
                  />
                ))
              )}
            </div>
            </>
          )}
          
              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              disabled={isExecuting}
              className="h-8"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={executeSelectedActions}
              disabled={nonMergeNoneSelected || isExecuting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
              data-testid="apply-actions-btn"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Apply {nonMergeSelectedCount} {nonMergeSelectedCount === 1 ? 'Action' : 'Actions'}
                </>
              )}
            </Button>
              </div>
              </ModalZIndexProvider>
            </div>
          </div>
        </div>
      )}
      
      {/* Merge Confirmation Modal - Separate modal for merge actions */}
      <MergeConfirmationModal
        isOpen={mergeModalOpen}
        onClose={() => {
          setMergeModalOpen(false);
          setActiveMergeAction(null);
          setActiveMergeIndex(null);
        }}
        action={activeMergeAction}
        actionIndex={activeMergeIndex}
        productId={productId}
        onMergeComplete={handleMergeComplete}
        onError={handleMergeError}
        logActionOutcome={logActionOutcome}
        user={user}
        zIndex={reviewModalZIndex}
        // Pass data from parent to avoid redundant API calls (same pattern as SplitFeatureModal)
        linkableEntities={{
          epic: relatedData.epics || [],
          release: relatedData.releases || [],
          persona: relatedData.personas || [],
          tag: relatedData.tags || [],
          user: relatedData.teamMembers || [],
          goal: relatedData.goals || [],
          initiative: relatedData.initiatives || [],
        }}
        // For feature merges, find source items from in-memory features array
        sourceItemsFromParent={
          activeMergeAction?.entity_type === 'feature' && relatedData.features
            ? activeMergeAction.item_ids?.map(id => 
                relatedData.features.find(f => f.feature_id === id)
              ).filter(Boolean)
            : undefined
        }
      />
    </>
  );
};

// =============================================================================
// UTILITY FUNCTIONS - Parse action blocks from AI responses
// =============================================================================

/**
 * Parse action block from AI response text
 * Extracts JSON action data from ```action-block``` markers
 */
export const parseActionBlock = (text) => {
  if (!text) return null;
  
  const actionBlockMatch = text.match(/```action-block\s*([\s\S]*?)```/);
  if (!actionBlockMatch) return null;
  
  try {
    const actionData = JSON.parse(actionBlockMatch[1].trim());
    
    // Normalize to consistent format
    if (actionData.actions && Array.isArray(actionData.actions)) {
      return {
        ...actionData,
        allActions: actionData.actions,
        type: actionData.actions[0]?.type || 'AWAIT_FEEDBACK',
      };
    }
    
    return actionData;
  } catch (e) {
    console.error('Failed to parse action block:', e);
    return null;
  }
};

/**
 * Remove action block from AI response text
 * Returns the text with ```action-block``` markers and content removed
 */
export const removeActionBlock = (text) => {
  if (!text) return '';
  return text.replace(/```action-block\s*[\s\S]*?```/g, '').trim();
};

export default UnifiedActionReview;
